import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ActivityReport,
  GoogleDriveAttachment,
  User,
} from "../types";
import { apiService } from "../services/apiService";
import { TaskDetailModal } from "../components/reports/TaskDetailModal";
import { ProjectFilesModal } from "../components/projects/ProjectFilesModal";
import { WorkflowStartModal } from "../components/workflow/WorkflowStartModal";
import AssignTaskModal from "../components/workflow/AssignTaskModal";
import { ConfirmDialog } from "../components/common/ConfirmDialog";
import { ManagerDashboardHeader } from "./manager-dashboard/ManagerDashboardHeader";
import { ActivitiesTab } from "./manager-dashboard/ActivitiesTab";
import { LeavesTab } from "./manager-dashboard/LeavesTab";
import { ProjectsTab } from "./manager-dashboard/ProjectsTab";
import { ReportsTab } from "./manager-dashboard/ReportsTab";
import { useManagerDashboardSharedData } from "./manager-dashboard/hooks/useManagerDashboardSharedData";
import { useActivitiesLeavesTabModel } from "./manager-dashboard/hooks/useActivitiesLeavesTabModel";
import { useProjectsTabModel } from "./manager-dashboard/hooks/useProjectsTabModel";
import { useReportsTabModel } from "./manager-dashboard/hooks/useReportsTabModel";
import {
  DocumentTextIcon,
  XMarkIcon,
  CheckCircleIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import toast from "../utils/toast";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { buildProjectCode } from "../utils/projectCode";
import { exportRowsToExcel } from "../utils/excel";
import {
  calculateProjectFinanceSummary,
  getProjectTaxSettings,
  resolveProjectFinanceInputs,
} from "../utils/projectFinance";
import {
  allocateOvertimeCosts,
  getOvertimeSettings,
  OvertimeCostSegment,
} from "../utils/overtimeCost";
import {
  ActivityTableColumnPreference,
  ActivityTableScreenKey,
  collectFormColumnPreferences,
  dedupeActivityTableColumns,
  getFormColumnValue,
  parseFormFieldPairs,
  normalizeActivityTableAsciiLabel,
  sanitizeActivityTableLabel,
} from "../utils/activityTableColumns";
import {
  ExchangeRatesByBase,
  convertCurrencyAmount,
  extractExchangeRates,
  formatCurrencyByCode,
  normalizeCurrencyCode,
} from "../utils/currency";
type MainTabType = "activities" | "tasks" | "leaves" | "projects" | "reports";
type ManagerDashboardLocationState = {
  mainTab?: MainTabType;
};
type ReportViewType = "project" | "activity" | "user" | "leave-hours";
type ManagerCommentTarget = {
  activityInstanceId: number;
  targetUserId?: number | null;
  assigneeName?: string;
  activityTitle?: string;
  projectName?: string;
};

type LatestManagerCommentRow = {
  id: number;
  activityInstanceId: number;
  targetUserId?: number | null;
  authorName?: string | null;
  comment?: string | null;
  createdAt?: string | null;
};
type ReportViewApiType = "project" | "activity" | "user";

const LEAVE_HOURS_COLUMN = "İzin-Saat";
const TASK_ACTIVITIES_FILTER = "TASK_ACTIVITIES";
const LEAVE_TYPE_FIELD_LABEL = "İzin Türü";

type ManagerDashboardVariant = "manager" | "company-activities";
const MANAGER_DASHBOARD_TABS: MainTabType[] = [
  "projects",
  "activities",
  "tasks",
  "leaves",
  "reports",
];
const MANAGER_ACTIVITY_TABLE_SCREEN_KEY: ActivityTableScreenKey =
  "manager_dashboard_activities";

const isMainTabType = (value: unknown): value is MainTabType =>
  typeof value === "string" &&
  MANAGER_DASHBOARD_TABS.includes(value as MainTabType);

type ActivityRowFilters = {
  startDate: string;
  endDate: string;
  assignee: string;
  startTime: string;
  endTime: string;
  minDurationHours: string;
  minCost: string;
  projectName: string;
  activityTitle: string;
  formContent: string;
  description: string;
  managerComment: string;
};

type LeaveRowFilters = {
  startDate: string;
  endDate: string;
  assignee: string;
  startTime: string;
  endTime: string;
  minDurationHours: string;
  formContent: string;
  description: string;
};

interface ManagerDashboardProps {
  variant?: ManagerDashboardVariant;
}

interface WorkflowInstanceItem {
  id: number;
  name: string;
  status: string;
  workflowContainerId: number;
  workflowContainerName: string;
  workflowContainerFormData?: Record<string, any>;
  startedAt: string;
  completedAt?: string;
  createdAt: string;
  templateWorkflowName?: string;
  templateWorkflowId?: number;
  workflowId?: number;
  totalSpentHours?: number;
  projectCode?: string;
  projectMembers?: number[];
}

type UserHourlyCostRateRow = {
  id: number;
  userId: number;
  startDate: string;
  endDate?: string | null;
  hourlyCost: number | null;
  currency?: string | null;
};

type CompanyHourlyCostRateRow = {
  id: number;
  startDate: string;
  endDate?: string | null;
  hourlyCost: number | null;
  currency?: string | null;
};

const normalizeSearchText = (value: string): string =>
  value.toLocaleLowerCase("tr-TR");

const getDateSearchTokens = (value: unknown): string[] => {
  if (typeof value !== "string") {
    return [];
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return [];
  }

  const parsedDate = new Date(trimmed);
  if (Number.isNaN(parsedDate.getTime())) {
    return [];
  }

  const tokens = [
    parsedDate.toLocaleDateString("tr-TR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }),
    parsedDate.toLocaleDateString("tr-TR", {
      day: "numeric",
      month: "long",
    }),
    parsedDate.toLocaleDateString("tr-TR", {
      day: "numeric",
      month: "short",
      year: "numeric",
    }),
    parsedDate.toLocaleDateString("tr-TR", {
      day: "numeric",
      month: "short",
    }),
    parsedDate.toLocaleDateString("tr-TR"),
  ];

  return tokens.map((token) => normalizeSearchText(token));
};

const isManualAssignedTaskActivity = (report: ActivityReport): boolean =>
  typeof report.workflowNodeId === "string" &&
  report.workflowNodeId.startsWith("manual-assign-");

const matchesFormDataSearch = (
  formData: unknown,
  searchLower: string,
  extraValues: string[] = [],
): boolean => {
  const tokens: string[] = [];

  extraValues.forEach((value) => {
    if (value) {
      tokens.push(normalizeSearchText(value));
    }
  });

  if (!formData) {
    return tokens.some((token) => token.includes(searchLower));
  }

  if (typeof formData === "string") {
    tokens.push(normalizeSearchText(formData));
    try {
      const parsed = JSON.parse(formData);
      if (parsed && typeof parsed === "object") {
        Object.values(parsed).forEach((value) => {
          tokens.push(...getDateSearchTokens(value));
        });
      }
    } catch {
      // ignore
    }
  } else {
    try {
      tokens.push(normalizeSearchText(JSON.stringify(formData)));
    } catch {
      // ignore
    }

    if (typeof formData === "object" && formData) {
      Object.values(formData).forEach((value) => {
        tokens.push(...getDateSearchTokens(value));
      });
    }
  }

  if (!tokens.length) {
    return false;
  }

  const normalizedTokens = tokens.map((token) => token.replace(/_/g, " "));
  return normalizedTokens.some((token) => token.includes(searchLower));
};

const parseIdList = (value?: string | null): number[] => {
  if (!value) {
    return [];
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return [];
  }

  try {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed)) {
      return parsed
        .map((item) => Number(item))
        .filter((item) => Number.isFinite(item));
    }
  } catch {
    // Fall through to string parsing
  }

  const cleaned = trimmed.replace(/[\[\]\s]/g, "");
  if (!cleaned) {
    return [];
  }

  return cleaned
    .split(",")
    .map((item) => Number(item.trim()))
    .filter((item) => Number.isFinite(item));
};

const hasKnownUser = (ids: number[], users: User[]): boolean =>
  ids.some((id) => users.some((user) => user.id === id));

const getPlannedHours = (formData: unknown): number => {
  if (!formData) {
    return 0;
  }

  let data: any = formData;
  if (typeof formData === "string") {
    try {
      data = JSON.parse(formData);
    } catch {
      return 0;
    }
  }

  const raw =
    data?.estimatedDurationHours ??
    data?.estimatedDurationDays ??
    data?.duration;
  if (raw === undefined || raw === null) {
    return 0;
  }

  const value = typeof raw === "string" ? Number.parseFloat(raw) : Number(raw);
  return Number.isFinite(value) ? value : 0;
};

export const ManagerDashboard: React.FC<ManagerDashboardProps> = ({
  variant = "manager",
}) => {
  const { t } = useTranslation([
    "dashboard",
    "tasks",
    "common",
    "errors",
    "projects",
    "workflows",
    "activities",
  ]);
  const location = useLocation();
  const navigate = useNavigate();
  const isCompanyActivitiesVariant = variant === "company-activities";
  const requestedMainTab = isMainTabType(
    (location.state as ManagerDashboardLocationState | null)?.mainTab,
  )
    ? (location.state as ManagerDashboardLocationState | null)?.mainTab
    : null;
  const {
    getWorkflowLabel,
    getSubmitDescriptionLabel,
    isAYCCompany,
    companySettings,
    applyCompanySettings,
    user,
    isAdmin,
    isManager,
  } = useAuth();
  const canViewCosts = isAdmin() || isManager();
  const overtimeSettings = useMemo(
    () => getOvertimeSettings(companySettings),
    [companySettings],
  );

  // Company-specific terminology
  const workflowText = getWorkflowLabel();
  const activitySubmitDescriptionLabel = getSubmitDescriptionLabel(
    t("dashboard:managerActivities.submitDescription"),
  );
  const isYapilanIsFormColumn = (
    column: ActivityTableColumnPreference,
  ): boolean => {
    if (column.kind !== "form") {
      return false;
    }

    const normalizedLabel =
      normalizeActivityTableAsciiLabel(column.label) ||
      (column.id.startsWith("form:")
        ? normalizeActivityTableAsciiLabel(column.id.slice(5))
        : "");

    return normalizedLabel === "yapilan is";
  };

  const getActivityFormColumnValue = (
    tooltip: string | null | undefined,
    column: ActivityTableColumnPreference,
    fallbackActivityTitle = "",
  ): string => {
    const formValue = getFormColumnValue(tooltip, column);
    if (formValue) {
      return formValue;
    }

    if (!isAYCCompany() || !isYapilanIsFormColumn(column)) {
      return "";
    }

    return (fallbackActivityTitle || "").trim();
  };

  const getActivityFormColumnReference = (
    columnId: string,
  ): ActivityTableColumnPreference => {
    const existingColumn = activityVisibleColumns.find(
      (column) => column.id === columnId,
    );

    return (
      existingColumn || {
        id: columnId,
        kind: "form",
        label: columnId.startsWith("form:") ? columnId.slice(5) : "",
      }
    );
  };

  const leaveSubmitDescriptionLabel = t("dashboard:managerLeaves.leaveDescription");

  const [mainTab, setMainTab] = useState<MainTabType>(() => {
    if (variant === "company-activities") {
      return "activities";
    }

    return requestedMainTab ?? "projects";
  });
  const appliedLocationTabKeyRef = useRef<string | null>(null);
  const isActivityLikeTab = mainTab === "activities" || mainTab === "tasks";
  const [filteredReports, setFilteredReports] = useState<ActivityReport[]>([]);
  const [selectedTask, setSelectedTask] = useState<ActivityReport | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTaskAttachments, setSelectedTaskAttachments] = useState<{
    task: ActivityReport;
    attachments: GoogleDriveAttachment[];
  } | null>(null);
  const [previewingDriveFileId, setPreviewingDriveFileId] = useState<string | null>(
    null,
  );
  const [selectedProjectForFiles, setSelectedProjectForFiles] = useState<{
    project: WorkflowInstanceItem;
    projectName: string;
    projectManagerId?: number | string | null;
  } | null>(null);
  const [showProjectFilesModal, setShowProjectFilesModal] = useState(false);
  const [showProjectEditModal, setShowProjectEditModal] = useState(false);
  const [showAssignTaskModal, setShowAssignTaskModal] = useState(false);
  const [showDeleteActivityConfirm, setShowDeleteActivityConfirm] =
    useState(false);
  const [activityReportToDelete, setActivityReportToDelete] =
    useState<ActivityReport | null>(null);

  const handlePreviewWorkflowAttachment = useCallback(
    async (attachment: GoogleDriveAttachment) => {
      if (!attachment?.id) return;

      const previewWindow = apiService.shouldOpenPreviewWindow(
        attachment.name,
        attachment.mimeType,
      )
        ? window.open("", "_blank")
        : null;
      if (previewWindow) {
        previewWindow.opener = null;
        apiService.preparePreviewWindow(previewWindow, attachment.name);
      }

      try {
        setPreviewingDriveFileId(attachment.id);
        await apiService.previewGoogleDriveFile(
          attachment.id,
          attachment.name || "preview",
          previewWindow,
        );
      } catch (error) {
        if (previewWindow && !previewWindow.closed) {
          previewWindow.close();
        }
        toast.error("Dosya görüntülenemedi.");
      } finally {
        setPreviewingDriveFileId(null);
      }
    },
    [],
  );
  const [deletingActivity, setDeletingActivity] = useState(false);
  const [selectedProjectForEdit, setSelectedProjectForEdit] =
    useState<WorkflowInstanceItem | null>(null);

  const [managerCommentTarget, setManagerCommentTarget] =
    useState<ManagerCommentTarget | null>(null);
  const [managerCommentText, setManagerCommentText] = useState("");
  const [savingManagerComment, setSavingManagerComment] = useState(false);
  const [
    latestManagerCommentsByActivityId,
    setLatestManagerCommentsByActivityId,
  ] = useState<Record<string, LatestManagerCommentRow>>({});
  const [isActivityTableConfiguratorOpen, setIsActivityTableConfiguratorOpen] =
    useState(false);
  const [isSavingActivityTableLayout, setIsSavingActivityTableLayout] =
    useState(false);
  const [dashboardExchangeRatesByBase, setDashboardExchangeRatesByBase] =
    useState<ExchangeRatesByBase>({});

  const {
    reports,
    projects,
    projectCollections,
    activityDefinitions,
    loading,
    reportsLoading,
    projectsLoading,
    users,
    userHourlyCostRates,
    companyAverageHourlyCostRates,
    fetchReports,
    fetchProjects,
    fetchUsers,
    fetchActivities,
    isLeaveActivityReport,
  } = useManagerDashboardSharedData({
    mainTab,
    user,
    canViewCosts,
    t,
  });

  const effectiveActivityReports = useMemo(
    () =>
      variant === "company-activities"
        ? reports.filter((report: ActivityReport) => report.activityStatus !== "PENDING_APPROVAL")
        : reports,
    [reports, variant],
  );

  const {
    searchQuery,
    setSearchQuery,
    selectedProject,
    setSelectedProject,
    statusFilter,
    setStatusFilter,
    workflowFilter,
    setWorkflowFilter,
    activityFilter,
    setActivityFilter,
    assigneeFilter,
    setAssigneeFilter,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    activityRowFilters,
    setActivityRowFilters,
    activityFormColumnFilters,
    setActivityFormColumnFilters,
    leaveRowFilters,
    setLeaveRowFilters,
    activityCurrentPage,
    setActivityCurrentPage,
    activityItemsPerPage,
    setActivityItemsPerPage,
    activitySummaryMode,
    setActivitySummaryMode,
    leaveSummaryMode,
    setLeaveSummaryMode,
    activitySortField,
    activitySortOrder,
    handleActivitySort,
    pendingCount,
    pendingApprovalCount,
    completedCount,
    rejectedCount,
    taskActivitiesCount,
    serverReportFilters,
    uniqueAssignees,
    uniqueWorkflows,
    uniqueProjects,
    uniqueActivities,
    clearActivityRowFilters,
    clearAllFilters,
    hasActiveFilters,
  } = useActivitiesLeavesTabModel({
    mainTab,
    reports: effectiveActivityReports,
    users,
    projects,
    activityDefinitions,
    taskActivitiesFilter: TASK_ACTIVITIES_FILTER,
    isManualAssignedTaskActivity,
  });

  useEffect(() => {
    if (isCompanyActivitiesVariant && startDate) {
      setStartDate("");
    }
  }, [isCompanyActivitiesVariant, setStartDate, startDate]);

  const needsReportDataset =
    mainTab === "activities" ||
    mainTab === "tasks" ||
    mainTab === "leaves" ||
    mainTab === "projects";

  const refreshReports = useCallback(async () => {
    if (!needsReportDataset) {
      return;
    }
    await fetchReports(serverReportFilters);
  }, [fetchReports, needsReportDataset, serverReportFilters]);

  useEffect(() => {
    if (!needsReportDataset) {
      return;
    }
    void refreshReports();
  }, [needsReportDataset, refreshReports]);

  useEffect(() => {
    if (!canViewCosts) {
      setDashboardExchangeRatesByBase({});
      return;
    }

    let isCancelled = false;

    const loadTryBasedExchangeRates = async () => {
      try {
        const payload = await apiService.getLatestExchangeRates("TRY");
        const extracted = extractExchangeRates(payload);

        if (isCancelled) {
          return;
        }

        if (extracted) {
          setDashboardExchangeRatesByBase({
            [extracted.baseCurrency]: extracted.rates,
          });
        } else {
          setDashboardExchangeRatesByBase({});
        }
      } catch (error) {
        if (!isCancelled) {
          setDashboardExchangeRatesByBase({});
        }
      }
    };

    void loadTryBasedExchangeRates();

    return () => {
      isCancelled = true;
    };
  }, [canViewCosts]);

  const formatDateForInput = (value: Date | null) =>
    value ? value.toISOString().slice(0, 10) : "";

  const formatTimeForInput = (value: Date | null) =>
    value
      ? `${String(value.getHours()).padStart(2, "0")}:${String(
          value.getMinutes(),
        ).padStart(2, "0")}`
      : "";

  const formatDateOnlyTr = (value: Date | null) =>
    value ? value.toLocaleDateString("tr-TR") : "-";

  const formatTimeOnlyTr = (value: Date | null) =>
    value
      ? `${String(value.getHours()).padStart(2, "0")}:${String(
          value.getMinutes(),
        ).padStart(2, "0")}`
      : "-";

  const formatHoursTr = (value: number) =>
    Number.isFinite(value) ? value.toFixed(2).replace(".", ",") : "-";

  const formatCurrencyTr = (value: number) =>
    Number.isFinite(value)
      ? formatCurrencyByCode(value, "TRY", { display: "suffix" })
      : "0,00 TL";

  const getProjectCurrencyForFormData = useCallback(
    (formData?: Record<string, any>) =>
      resolveProjectFinanceInputs(formData || {}).projectCurrency,
    [],
  );

  const convertTryAmountToCurrency = useCallback(
    (amount: number | null | undefined, currencyCode: string): number | null => {
      if (amount === null || amount === undefined || !Number.isFinite(amount)) {
        return null;
      }

      const normalizedCurrency = normalizeCurrencyCode(currencyCode);
      if (normalizedCurrency === "TRY") {
        return amount;
      }

      return convertCurrencyAmount(
        amount,
        "TRY",
        normalizedCurrency,
        dashboardExchangeRatesByBase,
      );
    },
    [dashboardExchangeRatesByBase],
  );

  const convertTryAmountForProject = useCallback(
    (formData: Record<string, any>, amount: number | null | undefined) =>
      convertTryAmountToCurrency(
        amount,
        getProjectCurrencyForFormData(formData),
      ),
    [convertTryAmountToCurrency, getProjectCurrencyForFormData],
  );

  const convertAmountToTry = useCallback(
    (amount: number | null | undefined, currencyCode: string): number | null =>
      convertCurrencyAmount(
        amount,
        normalizeCurrencyCode(currencyCode),
        "TRY",
        dashboardExchangeRatesByBase,
      ),
    [dashboardExchangeRatesByBase],
  );

  const parseDateTimeString = (value?: string | null): Date | null => {
    if (!value || typeof value !== "string") {
      return null;
    }
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    const normalized = trimmed.includes("T")
      ? trimmed
      : trimmed.replace(" ", "T");
    const date = new Date(normalized);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  const parseRateDateBoundary = (
    value: string | null | undefined,
    endOfDay = false,
  ): Date | null => {
    if (!value || typeof value !== "string") return null;
    return parseDateTimeString(
      `${value}${endOfDay ? "T23:59:59" : "T00:00:00"}`,
    );
  };

  const isDateWithinRate = (
    targetDate: Date,
    startDate: string,
    endDate?: string | null,
  ): boolean => {
    const start = parseRateDateBoundary(startDate, false);
    const end = endDate
      ? parseRateDateBoundary(endDate, true)
      : parseRateDateBoundary("9999-12-31", true);
    if (!start || !end) return false;
    return targetDate >= start && targetDate <= end;
  };

  const getAssigneeLabelForExport = (report: ActivityReport): string => {
    // Mirror the "Çalışan Kişi" label logic used in the activities table.
    if (report.completedUserIds) {
      try {
        const completedIds = JSON.parse(report.completedUserIds);
        if (Array.isArray(completedIds) && completedIds.length > 0) {
          const names = completedIds
            .map((id) => {
              const userItem = users.find((u) => u.id === id);
              return userItem
                ? `${userItem.firstName} ${userItem.lastName}`
                : null;
            })
            .filter(Boolean)
            .join(", ");
          if (names) return names;
        }
      } catch {
        // ignore
      }
    }

    if (report.assignedUsers) {
      try {
        const cleaned = report.assignedUsers.replace(/[\[\]\s]/g, "");
        if (cleaned) {
          const ids = cleaned
            .split(",")
            .filter(Boolean)
            .map((id) => Number(id.trim()))
            .filter((n) => Number.isFinite(n));
          if (ids.length > 0) {
            const names = ids
              .map((id) => {
                const userItem = users.find((u) => u.id === id);
                return userItem
                  ? `${userItem.firstName} ${userItem.lastName}`
                  : null;
              })
              .filter(Boolean)
              .join(", ");
            if (names) return names;
          }
        }
      } catch {
        // ignore
      }
    }

    return report.assigneeName || "-";
  };

  const exportActivityTabToExcel = () => {
    if (!activityTabReports.length) return;

    const isLeave = mainTab === "leaves";
    let headers: Array<string> = isLeave
      ? [
          "Sıra No",
          "İzin Başlangıç Tarihi",
          "İzin Bitiş Tarihi",
          "İzin Alan Kişi",
          "İzin Başlangıç Saati",
          "İzin Bitiş Saati",
          "Süre (Saat)",
          "Form İçeriği",
          leaveSubmitDescriptionLabel,
          "Yönetici Yorumu",
        ]
      : activityVisibleColumns.map((column) => column.label);

    if (isLeave) {
      headers = [
        t("dashboard:managerLeaves.rowNo"),
        t("dashboard:managerLeaves.leaveStartDate"),
        t("dashboard:managerLeaves.leaveEndDate"),
        t("dashboard:managerLeaves.leaveAssignee"),
        t("dashboard:managerLeaves.leaveStartTime"),
        t("dashboard:managerLeaves.leaveEndTime"),
        t("dashboard:managerLeaves.durationHours"),
        "İzin Türü",
        leaveSubmitDescriptionLabel,
        t("dashboard:managerLeaves.managerComment"),
      ];
    }

    const rows: Array<Array<string | number>> = [headers];

    activityTabReports.forEach((report, idx) => {
      const rowNo = idx + 1;
      const { start, end } = getReportStartEnd(report);
      const duration = getReportDuration(report);
      const content = getReportFormContent(report);
      const leaveType = getReportLeaveType(report);
      const description = getReportSubmitDescription(report);
      const assignee = getAssigneeLabelForExport(report);
      const latestComment =
        latestManagerCommentsByActivityId[String(report.activityId)]?.comment ??
        "";

      const row: Array<string | number> = isLeave
        ? [
            rowNo,
            formatDateOnlyTr(start),
            formatDateOnlyTr(end),
            assignee === "-" ? "" : assignee,
            formatTimeOnlyTr(start),
            formatTimeOnlyTr(end),
            duration > 0 ? Number(duration.toFixed(2)) : "",
            leaveType.tooltip || leaveType.summary || "",
            description === "-" ? "" : description,
            latestComment.toString().trim(),
          ]
        : activityVisibleColumns.map((column) => {
            if (column.kind === "form") {
              return (
                getActivityFormColumnValue(
                  content.tooltip,
                  column,
                  report.activityInstanceName || report.activityTitle || "",
                ) || ""
              );
            }

            switch (column.id) {
              case "rowNo":
                return rowNo;
              case "endDate":
                return formatDateOnlyTr(end);
              case "assignee":
                return assignee === "-" ? "" : assignee;
              case "startTime":
                return formatTimeOnlyTr(start);
              case "endTimeOnly":
                return formatTimeOnlyTr(end);
              case "duration":
                return duration > 0 ? Number(duration.toFixed(2)) : "";
              case "cost":
                return Number(
                  (
                    activityCostByActivityId.get(report.activityId) ?? 0
                  ).toFixed(2),
                );
              case "projectName":
                return report.projectName || "";
              case "activityTitle":
                return report.activityTitle || "";
              case "formContent":
                return content.tooltip || content.summary || "";
              case "submitDescription":
                return description === "-" ? "" : description;
              case "managerComment":
                return latestComment.toString().trim();
              case "details":
                return "";
              default:
                return "";
            }
          });

      rows.push(row);
    });

    const summaryMode = isLeave ? leaveSummaryMode : activitySummaryMode;
    const summaryHours = isLeave
      ? summaryMode === "average"
        ? filteredLeavesAverageDuration
        : filteredLeavesTotalDuration
      : summaryMode === "average"
        ? filteredActivitiesAverageDuration
        : filteredActivitiesTotalDuration;
    const summaryLabel =
      summaryMode === "average"
        ? t("dashboard:managerActivities.filteredAverage")
        : t("dashboard:managerActivities.filteredTotal");
    const summaryRow = new Array(headers.length).fill("");
    summaryRow[0] = summaryLabel;
    if (isLeave) {
      summaryRow[6] = summaryHours > 0 ? Number(summaryHours.toFixed(2)) : "-";
    } else {
      const durationIndex = activityVisibleColumns.findIndex(
        (column) => column.kind === "default" && column.id === "duration",
      );
      if (durationIndex >= 0) {
        summaryRow[durationIndex] =
          summaryHours > 0 ? Number(summaryHours.toFixed(2)) : "-";
      }
    }
    if (!isLeave && canViewCosts) {
      const summaryCost =
        activitySummaryMode === "average"
          ? filteredActivitiesAverageCost
          : filteredActivitiesTotalCost;
      const costIndex = activityVisibleColumns.findIndex(
        (column) => column.kind === "default" && column.id === "cost",
      );
      if (costIndex >= 0) {
        summaryRow[costIndex] =
          summaryCost > 0 ? Number(summaryCost.toFixed(2)) : "-";
      }
    }
    summaryRow[headers.length - 1] = isLeave
      ? t("dashboard:managerLeaves.recordCount", {
          count: activityTabReports.length,
        })
      : t("dashboard:managerActivities.recordCount", {
          count: activityTabReports.length,
        });
    rows.push(summaryRow);

    const dateStamp = new Date().toISOString().slice(0, 10);
    const fileName = isLeave
      ? `${t("dashboard:managerLeaves.excelFilePrefix")}_${dateStamp}.xlsx`
      : `${t("dashboard:managerActivities.excelFilePrefix")}_${dateStamp}.xlsx`;
    exportRowsToExcel({
      fileName,
      sheetName: isLeave
        ? t("dashboard:managerLeaves.sheetName")
        : t("dashboard:managerActivities.sheetName"),
      rows,
    });
  };

  const getProjectFinanceSummaryForDashboard = useCallback(
    (
      formData: Record<string, any>,
      realizedCostTry: number,
      totalEstimatedCostTry: number | null,
      totalCollectedTry: number | null = null,
    ) => {
      const { vatRate, corporateTaxRate } =
        getProjectTaxSettings(companySettings);
      const financeInputs = resolveProjectFinanceInputs(formData);
      const realizedCost = convertTryAmountToCurrency(
        realizedCostTry,
        financeInputs.projectCurrency,
      );
      const totalEstimatedCost =
        totalEstimatedCostTry === null
          ? null
          : convertTryAmountToCurrency(
              totalEstimatedCostTry,
              financeInputs.projectCurrency,
            );
      const totalCollected =
        totalCollectedTry === null
          ? null
          : convertTryAmountToCurrency(
              totalCollectedTry,
              financeInputs.projectCurrency,
            );

      return calculateProjectFinanceSummary({
        ...financeInputs,
        vatRate,
        corporateTaxRate,
        realizedCost:
          realizedCost !== null ? realizedCost : Number.NaN,
        totalEstimatedCost,
        totalCollected,
      });
    },
    [companySettings, convertTryAmountToCurrency],
  );

  const activityFilterCacheRef = useRef(
    new Map<
      number,
      {
        assigneeNames: Array<string>;
        content: { summary: string; tooltip: string };
        leaveType: { summary: string; tooltip: string };
        description: string;
        duration: number;
        managerCommentValue: string;
        searchBlob: string;
        startDateValue: string;
        endDateValue: string;
        startTimeValue: string;
        endTimeValue: string;
      }
    >(),
  );

  useEffect(() => {
    activityFilterCacheRef.current.clear();
  }, [
    effectiveActivityReports,
    users,
    activityDefinitions,
    latestManagerCommentsByActivityId,
  ]);

  function getActivityFilterCache(report: ActivityReport) {
    const existing = activityFilterCacheRef.current.get(report.activityId);
    if (existing) {
      return existing;
    }

    const content = getReportFormContent(report);
    const leaveType = getReportLeaveType(report);
    const description = getReportSubmitDescription(report);
    const duration = getReportDuration(report);
    const { start, end } = getReportStartEnd(report);
    const managerCommentValue = normalizeSearchText(
      latestManagerCommentsByActivityId[String(report.activityId)]?.comment ||
        "",
    );

    const assigneeNames = Array.from(
      new Set(
        [report.assigneeName, ...parseIdList(report.assignedUsers).map((id) => {
          const user = users.find((item) => item.id === id);
          return user ? `${user.firstName} ${user.lastName}` : "";
        })]
          .map((name) => (name || "").trim())
          .filter(Boolean),
      ),
    );

    const cache = {
      assigneeNames,
      content,
      leaveType,
      description,
      duration,
      managerCommentValue,
      searchBlob: normalizeSearchText(
        [
          report.activityTitle,
          report.projectName,
          report.workflowName,
          report.assigneeName,
          assigneeNames.join(" "),
          content.summary,
          content.tooltip,
          leaveType.summary,
          leaveType.tooltip,
          description,
          managerCommentValue,
          start ? formatDateOnlyTr(start) : "",
          end ? formatDateOnlyTr(end) : "",
          start ? formatTimeOnlyTr(start) : "",
          end ? formatTimeOnlyTr(end) : "",
          duration > 0 ? duration.toFixed(2) : "",
        ]
          .filter(Boolean)
          .join(" "),
      ),
      startDateValue:
        start && !Number.isNaN(start.getTime())
          ? `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, "0")}-${String(start.getDate()).padStart(2, "0")}`
          : "",
      endDateValue:
        end && !Number.isNaN(end.getTime())
          ? `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, "0")}-${String(end.getDate()).padStart(2, "0")}`
          : "",
      startTimeValue:
        start && !Number.isNaN(start.getTime())
          ? `${String(start.getHours()).padStart(2, "0")}:${String(start.getMinutes()).padStart(2, "0")}`
          : "",
      endTimeValue:
        end && !Number.isNaN(end.getTime())
          ? `${String(end.getHours()).padStart(2, "0")}:${String(end.getMinutes()).padStart(2, "0")}`
          : "",
    };

    activityFilterCacheRef.current.set(report.activityId, cache);
    return cache;
  }

  // Apply all filters on top of reports
  useEffect(() => {
    // First apply project filter if selected
    let baseFiltered = effectiveActivityReports;
    if (selectedProject && mainTab !== "leaves") {
      baseFiltered = effectiveActivityReports.filter(
        (r) =>
          r.workflowId === selectedProject.workflowId &&
          r.projectName === selectedProject.projectName,
      );
    }

    // Apply workflow filter
    if (workflowFilter !== "all" && mainTab !== "leaves") {
      baseFiltered = baseFiltered.filter(
        (r) => r.workflowName === workflowFilter,
      );
    }

    // Apply status filter
    if (statusFilter !== "all" && mainTab !== "leaves") {
      if (statusFilter === TASK_ACTIVITIES_FILTER) {
        baseFiltered = baseFiltered.filter(isManualAssignedTaskActivity);
      } else if (statusFilter === "PENDING") {
        baseFiltered = baseFiltered.filter(
          (r) =>
            r.activityStatus === "PENDING" || r.activityStatus === "ACTIVE",
        );
      } else {
        baseFiltered = baseFiltered.filter(
          (r) => r.activityStatus === statusFilter,
        );
      }
    } else if (isActivityLikeTab) {
      baseFiltered = baseFiltered.filter(
        (r) => r.activityStatus !== "REJECTED",
      );
    }

    // Remove unassigned activities from the activities table
    baseFiltered = baseFiltered.filter((r) => {
      if (r.assigneeName && r.assigneeName.trim()) {
        return true;
      }
      if (statusFilter === "COMPLETED") {
        const completedIds = parseIdList(r.completedUserIds);
        return hasKnownUser(completedIds, users);
      }
      const assignedIds = parseIdList(r.assignedUsers);
      return hasKnownUser(assignedIds, users);
    });

    // Apply assignee filter (including multi-user activities)
    if (assigneeFilter !== "all") {
      baseFiltered = baseFiltered.filter((r) => {
        if (r.assigneeName === assigneeFilter) {
          return true;
        }
        return getActivityFilterCache(r).assigneeNames.includes(assigneeFilter);
      });
    }

    // Apply date range filter
    if (startDate || endDate) {
      if (isCompanyActivitiesVariant) {
        const selectedDate = endDate || startDate;
        baseFiltered = baseFiltered.filter((r) => {
          if (!selectedDate) {
            return true;
          }

          return getActivityFilterCache(r).endDateValue === selectedDate;
        });
      } else {
        baseFiltered = baseFiltered.filter((r) => {
          // Customer expectation: filter by the actual worked time range (start/end time entered),
          // not by when the activity instance was created.
          const getWorkedRanges = (
            userDurations: string | undefined,
            baseDateForTimeOnly: Date | null,
          ): Array<{ start: Date; end: Date }> => {
            if (!userDurations) return [];
            try {
              const parsed = JSON.parse(userDurations);
              if (!parsed || typeof parsed !== "object") return [];

              const toDateTime = (value: any): Date | null => {
                if (!value || typeof value !== "string") return null;
                const trimmed = value.trim();
                // Full datetime formats: "YYYY-MM-DD HH:mm" or ISO
                const parsedDateTime = parseDateTimeString(trimmed);
                if (parsedDateTime) return parsedDateTime;

                // Legacy time-only format: "HH:mm"
                const timeOnlyMatch = trimmed.match(/^(\d{1,2}):(\d{2})$/);
                if (!timeOnlyMatch || !baseDateForTimeOnly) return null;
                const hours = Number(timeOnlyMatch[1]);
                const minutes = Number(timeOnlyMatch[2]);
                if (
                  Number.isNaN(hours) ||
                  Number.isNaN(minutes) ||
                  hours < 0 ||
                  hours > 23 ||
                  minutes < 0 ||
                  minutes > 59
                ) {
                  return null;
                }
                return new Date(
                  baseDateForTimeOnly.getFullYear(),
                  baseDateForTimeOnly.getMonth(),
                  baseDateForTimeOnly.getDate(),
                  hours,
                  minutes,
                  0,
                  0,
                );
              };

              return Object.keys(parsed)
                .map((userId) => {
                  const userData = (parsed as any)[userId];
                  if (!userData || typeof userData !== "object") return null;
                  const start = toDateTime(userData.startTime);
                  const endRaw = toDateTime(userData.endTime);
                  if (!start || !endRaw) return null;

                  // If legacy time-only crosses midnight, treat end as next day.
                  const end =
                    endRaw <= start
                      ? new Date(endRaw.getTime() + 24 * 60 * 60 * 1000)
                      : endRaw;
                  if (!start || !end || end <= start) return null;
                  return { start, end };
                })
                .filter(Boolean) as Array<{ start: Date; end: Date }>;
            } catch {
              return [];
            }
          };

          const parseDateFilter = (value: string, endOfDay: boolean) => {
            const parts = value.split("-").map((part) => Number(part));
            if (parts.length !== 3 || parts.some(Number.isNaN)) return null;
            const [year, month, day] = parts;
            return new Date(
              year,
              month - 1,
              day,
              endOfDay ? 23 : 0,
              endOfDay ? 59 : 0,
              endOfDay ? 59 : 0,
              endOfDay ? 999 : 0,
            );
          };
          const start = startDate ? parseDateFilter(startDate, false) : null;
          const end = endDate ? parseDateFilter(endDate, true) : null;

          // Determine the activity's actual time range(s) from userDurations.
          // For multi-user activities, include the report if ANY user's worked range overlaps the filter.
          const baseForTimeOnly =
            parseDateTimeString(r.activityCompletedAt || r.activityCreatedAt) ||
            (r.activityCompletedAt
              ? new Date(r.activityCompletedAt)
              : new Date(r.activityCreatedAt));

          const workedRanges = getWorkedRanges(r.userDurations, baseForTimeOnly);
          const fallbackStart =
            parseDateTimeString(r.activityCreatedAt) ||
            new Date(r.activityCreatedAt);
          const fallbackEnd = r.activityCompletedAt
            ? parseDateTimeString(r.activityCompletedAt) ||
              new Date(r.activityCompletedAt)
            : fallbackStart;

          const rangesToCheck =
            workedRanges.length > 0
              ? workedRanges
              : [{ start: fallbackStart, end: fallbackEnd }];

          const overlaps = (rangeStart: Date, rangeEnd: Date) => {
            if (start && end) {
              return rangeEnd >= start && rangeStart <= end;
            }
            if (start) {
              return rangeEnd >= start;
            }
            if (end) {
              return rangeStart <= end;
            }
            return true;
          };

          return rangesToCheck.some((range) => overlaps(range.start, range.end));
        });
      }
    }

    // Then apply search filter
    let finalFiltered = baseFiltered;
    if (searchQuery.trim() !== "") {
      const query = searchQuery.toLocaleLowerCase("tr-TR");
      finalFiltered = baseFiltered.filter(
        (report) => getActivityFilterCache(report).searchBlob.includes(query),
      );
    }

    // Apply row filters for Activities/Leaves tables
    if (isActivityLikeTab) {
      finalFiltered = finalFiltered.filter((report) => {
        const cache = getActivityFilterCache(report);
        const endDateValue = cache?.endDateValue || "";
        const startTimeValue = cache?.startTimeValue || "";
        const endTimeValue = cache?.endTimeValue || "";
        const assigneeValue = normalizeSearchText(report.assigneeName || "");
        const projectValue = normalizeSearchText(report.projectName || "");
        const activityValue = normalizeSearchText(report.activityTitle || "");
        const formContentValue = normalizeSearchText(cache?.content.summary || "");
        const descriptionValue = normalizeSearchText(cache?.description || "");
        const formColumnValues = Object.entries(activityFormColumnFilters);

        if (activityRowFilters.endDate) {
          if (
            !endDateValue ||
            (isCompanyActivitiesVariant
              ? endDateValue !== activityRowFilters.endDate
              : endDateValue > activityRowFilters.endDate)
          ) {
            return false;
          }
        }

        if (activityRowFilters.startTime) {
          if (
            !startTimeValue ||
            !startTimeValue.startsWith(activityRowFilters.startTime)
          ) {
            return false;
          }
        }

        if (activityRowFilters.endTime) {
          if (
            !endTimeValue ||
            !endTimeValue.startsWith(activityRowFilters.endTime)
          ) {
            return false;
          }
        }

        if (activityRowFilters.minDurationHours) {
          const minDuration = Number.parseFloat(
            activityRowFilters.minDurationHours,
          );
          if (Number.isFinite(minDuration) && (cache?.duration || 0) < minDuration) {
            return false;
          }
        }

        if (activityRowFilters.minCost) {
          const minCost = Number.parseFloat(
            activityRowFilters.minCost.replace(",", "."),
          );
          const activityCost =
            activityCostByActivityId.get(report.activityId) ?? 0;
          if (Number.isFinite(minCost) && activityCost < minCost) {
            return false;
          }
        }

        if (activityRowFilters.assignee.trim()) {
          const query = normalizeSearchText(activityRowFilters.assignee.trim());
          if (!assigneeValue.includes(query)) {
            return false;
          }
        }

        if (activityRowFilters.projectName.trim()) {
          const query = normalizeSearchText(
            activityRowFilters.projectName.trim(),
          );
          if (!projectValue.includes(query)) {
            return false;
          }
        }

        if (activityRowFilters.activityTitle.trim()) {
          const query = normalizeSearchText(
            activityRowFilters.activityTitle.trim(),
          );
          if (!activityValue.includes(query)) {
            return false;
          }
        }

        if (activityRowFilters.formContent.trim()) {
          const query = normalizeSearchText(
            activityRowFilters.formContent.trim(),
          );
          if (!formContentValue.includes(query)) {
            return false;
          }
        }

        if (activityRowFilters.description.trim()) {
          const query = normalizeSearchText(
            activityRowFilters.description.trim(),
          );
          if (!descriptionValue.includes(query)) {
            return false;
          }
        }

        if (activityRowFilters.managerComment.trim()) {
          const query = normalizeSearchText(
            activityRowFilters.managerComment.trim(),
          );
          const managerCommentValue = cache?.managerCommentValue || "";
          if (!managerCommentValue.includes(query)) {
            return false;
          }
        }

        for (const [columnId, rawFilterValue] of formColumnValues) {
          const filterValue = rawFilterValue.trim();
          if (!filterValue) {
            continue;
          }

          const columnValue = normalizeSearchText(
            getActivityFormColumnValue(
              cache?.content.tooltip || "",
              getActivityFormColumnReference(columnId),
              report.activityInstanceName || report.activityTitle || "",
            ) || "",
          );
          const query = normalizeSearchText(filterValue);
          if (!columnValue.includes(query)) {
            return false;
          }
        }

        return true;
      });
    } else if (mainTab === "leaves") {
      finalFiltered = finalFiltered.filter((report) => {
        const cache = getActivityFilterCache(report);
        const startDateValue = cache?.startDateValue || "";
        const endDateValue = cache?.endDateValue || "";
        const startTimeValue = cache?.startTimeValue || "";
        const endTimeValue = cache?.endTimeValue || "";
        const assigneeValue = normalizeSearchText(report.assigneeName || "");
        const formContentValue = normalizeSearchText(
          cache?.leaveType?.summary || "",
        );
        const descriptionValue = normalizeSearchText(cache?.description || "");

        if (leaveRowFilters.startDate) {
          if (!startDateValue || startDateValue < leaveRowFilters.startDate) {
            return false;
          }
        }

        if (leaveRowFilters.endDate) {
          if (!endDateValue || endDateValue > leaveRowFilters.endDate) {
            return false;
          }
        }

        if (leaveRowFilters.startTime) {
          if (!startTimeValue || startTimeValue !== leaveRowFilters.startTime) {
            return false;
          }
        }

        if (leaveRowFilters.endTime) {
          if (!endTimeValue || endTimeValue !== leaveRowFilters.endTime) {
            return false;
          }
        }

        if (leaveRowFilters.minDurationHours) {
          const minDuration = Number.parseFloat(
            leaveRowFilters.minDurationHours,
          );
          if (Number.isFinite(minDuration) && (cache?.duration || 0) < minDuration) {
            return false;
          }
        }

        if (leaveRowFilters.assignee.trim()) {
          const query = normalizeSearchText(leaveRowFilters.assignee.trim());
          if (!assigneeValue.includes(query)) {
            return false;
          }
        }

        if (leaveRowFilters.formContent.trim()) {
          const query = normalizeSearchText(leaveRowFilters.formContent.trim());
          if (!formContentValue.includes(query)) {
            return false;
          }
        }

        if (leaveRowFilters.description.trim()) {
          const query = normalizeSearchText(leaveRowFilters.description.trim());
          if (!descriptionValue.includes(query)) {
            return false;
          }
        }

        return true;
      });
    }

    // Apply activity filter (activities tab only)
    if (isActivityLikeTab && activityFilter !== "all") {
      finalFiltered = finalFiltered.filter(
        (r) => (r.activityTitle || "").trim() === activityFilter,
      );
    }

    // Apply sorting (only if activitySortOrder is not null)
    let sorted = [...finalFiltered];
    if (activitySortOrder) {
      sorted.sort((a, b) => {
        let aValue: any;
        let bValue: any;

        if (activitySortField === "activityTitle") {
          aValue = (a.activityTitle || "").toLowerCase();
          bValue = (b.activityTitle || "").toLowerCase();
        } else if (activitySortField === "projectName") {
          aValue = (a.projectName || "").toLowerCase();
          bValue = (b.projectName || "").toLowerCase();
        } else if (activitySortField === "workflowName") {
          aValue = (a.workflowName || "").toLowerCase();
          bValue = (b.workflowName || "").toLowerCase();
        } else if (activitySortField === "assignedTo") {
          aValue = (a.assigneeName || "").toLowerCase();
          bValue = (b.assigneeName || "").toLowerCase();
        } else if (activitySortField === "status") {
          aValue = (a.activityStatus || "").toLowerCase();
          bValue = (b.activityStatus || "").toLowerCase();
        } else if (activitySortField === "activityCreatedAt") {
          const aStart = getReportStartEnd(a).start;
          const bStart = getReportStartEnd(b).start;
          aValue = aStart ? aStart.getTime() : 0;
          bValue = bStart ? bStart.getTime() : 0;
        } else if (activitySortField === "completedAt") {
          const aEnd = getReportStartEnd(a).end;
          const bEnd = getReportStartEnd(b).end;
          aValue = aEnd ? aEnd.getTime() : 0;
          bValue = bEnd ? bEnd.getTime() : 0;
        } else if (activitySortField === "durationHours") {
          aValue = getReportDuration(a);
          bValue = getReportDuration(b);
        } else if (activitySortField === "costAmount") {
          aValue = activityCostByActivityId.get(a.activityId) ?? 0;
          bValue = activityCostByActivityId.get(b.activityId) ?? 0;
        } else if (activitySortField === "managerComment") {
          aValue = normalizeSearchText(
            latestManagerCommentsByActivityId[String(a.activityId)]?.comment ||
              "",
          );
          bValue = normalizeSearchText(
            latestManagerCommentsByActivityId[String(b.activityId)]?.comment ||
              "",
          );
        } else if (activitySortField.startsWith("form:")) {
          const columnReference =
            getActivityFormColumnReference(activitySortField);
          aValue = normalizeSearchText(
            getActivityFormColumnValue(
              getReportFormContent(a).tooltip,
              columnReference,
              a.activityInstanceName || a.activityTitle || "",
            ) || "",
          );
          bValue = normalizeSearchText(
            getActivityFormColumnValue(
              getReportFormContent(b).tooltip,
              columnReference,
              b.activityInstanceName || b.activityTitle || "",
            ) || "",
          );
        } else {
          aValue = 0;
          bValue = 0;
        }

        if (aValue < bValue) return activitySortOrder === "asc" ? -1 : 1;
        if (aValue > bValue) return activitySortOrder === "asc" ? 1 : -1;
        return 0;
      });
    }

    setFilteredReports(sorted);
  }, [
    searchQuery,
    effectiveActivityReports,
    selectedProject,
    statusFilter,
    workflowFilter,
    activityFilter,
    assigneeFilter,
    startDate,
    endDate,
    activitySortField,
    activitySortOrder,
    mainTab,
    users,
    activityRowFilters,
    activityFormColumnFilters,
    latestManagerCommentsByActivityId,
    isActivityLikeTab,
    isCompanyActivitiesVariant,
    leaveRowFilters,
    userHourlyCostRates,
    companyAverageHourlyCostRates,
  ]);

  useEffect(() => {
    setActivityCurrentPage(1);
  }, [
    searchQuery,
    selectedProject,
    statusFilter,
    workflowFilter,
    activityFilter,
    assigneeFilter,
    startDate,
    endDate,
    activityRowFilters,
    activityFormColumnFilters,
    leaveRowFilters,
    activityItemsPerPage,
  ]);

  const userRatesByUserId = useMemo(() => {
    const map = new Map<number, UserHourlyCostRateRow[]>();
    userHourlyCostRates.forEach((rate) => {
      if (
        typeof rate.userId !== "number" ||
        typeof rate.hourlyCost !== "number" ||
        !Number.isFinite(rate.hourlyCost) ||
        rate.hourlyCost < 0
      ) {
        return;
      }
      if ((rate.currency || "TRY").toUpperCase() !== "TRY") {
        return;
      }
      const existing = map.get(rate.userId) || [];
      existing.push(rate);
      map.set(rate.userId, existing);
    });

    map.forEach((rows, userId) => {
      const sorted = [...rows].sort((a, b) => {
        const aTime = parseRateDateBoundary(a.startDate)?.getTime() || 0;
        const bTime = parseRateDateBoundary(b.startDate)?.getTime() || 0;
        return bTime - aTime;
      });
      map.set(userId, sorted);
    });

    return map;
  }, [userHourlyCostRates]);

  const sortedAverageRates = useMemo(() => {
    return [...companyAverageHourlyCostRates]
      .filter(
        (rate) =>
          typeof rate.hourlyCost === "number" &&
          Number.isFinite(rate.hourlyCost) &&
          rate.hourlyCost >= 0 &&
          (rate.currency || "TRY").toUpperCase() === "TRY",
      )
      .sort((a, b) => {
        const aTime = parseRateDateBoundary(a.startDate)?.getTime() || 0;
        const bTime = parseRateDateBoundary(b.startDate)?.getTime() || 0;
        return bTime - aTime;
      });
  }, [companyAverageHourlyCostRates]);

  const getApplicableAverageRate = useCallback(
    (date: Date | null): number | null => {
      if (!canViewCosts || !date) return null;
      for (const rate of sortedAverageRates) {
        if (!rate.startDate || typeof rate.hourlyCost !== "number") {
          continue;
        }
        if (isDateWithinRate(date, rate.startDate, rate.endDate)) {
          return rate.hourlyCost;
        }
      }
      return null;
    },
    [canViewCosts, sortedAverageRates],
  );

  const resolveApplicableHourlyRate = useCallback(
    (userId: number | null | undefined, date: Date | null): number | null => {
      if (!canViewCosts || !date) return null;

      if (typeof userId === "number") {
        const userRates = userRatesByUserId.get(userId) || [];
        for (const rate of userRates) {
          if (
            !rate.startDate ||
            typeof rate.hourlyCost !== "number" ||
            !Number.isFinite(rate.hourlyCost)
          ) {
            continue;
          }
          if (isDateWithinRate(date, rate.startDate, rate.endDate)) {
            return rate.hourlyCost;
          }
        }
      }

      return getApplicableAverageRate(date);
    },
    [canViewCosts, getApplicableAverageRate, userRatesByUserId],
  );

  const getReportStartEnd = (
    report: ActivityReport,
  ): { start: Date | null; end: Date | null } => {
    const baseDateForTimeOnly =
      parseDateTimeString(
        report.activityCompletedAt || report.activityCreatedAt,
      ) ||
      (report.activityCompletedAt
        ? new Date(report.activityCompletedAt)
        : new Date(report.activityCreatedAt));

    const toDateTime = (value: any): Date | null => {
      if (!value || typeof value !== "string") return null;
      const trimmed = value.trim();
      const parsedDateTime = parseDateTimeString(trimmed);
      if (parsedDateTime) return parsedDateTime;

      // Legacy time-only format: "HH:mm"
      const timeOnlyMatch = trimmed.match(/^(\d{1,2}):(\d{2})$/);
      if (!timeOnlyMatch) return null;
      const hours = Number(timeOnlyMatch[1]);
      const minutes = Number(timeOnlyMatch[2]);
      if (
        Number.isNaN(hours) ||
        Number.isNaN(minutes) ||
        hours < 0 ||
        hours > 23 ||
        minutes < 0 ||
        minutes > 59
      ) {
        return null;
      }
      return new Date(
        baseDateForTimeOnly.getFullYear(),
        baseDateForTimeOnly.getMonth(),
        baseDateForTimeOnly.getDate(),
        hours,
        minutes,
        0,
        0,
      );
    };

    // Prefer user-entered ranges (earliest start / latest end across users).
    if (report.userDurations) {
      try {
        const parsed = JSON.parse(report.userDurations);
        if (parsed && typeof parsed === "object") {
          let earliest: Date | null = null;
          let latest: Date | null = null;

          Object.keys(parsed).forEach((userId) => {
            const userData = (parsed as any)[userId];
            if (!userData || typeof userData !== "object") return;

            const start = toDateTime((userData as any).startTime);
            const endRaw = toDateTime((userData as any).endTime);
            if (!start || !endRaw) return;
            const end =
              endRaw <= start
                ? new Date(endRaw.getTime() + 24 * 60 * 60 * 1000)
                : endRaw;
            if (end <= start) return;

            if (!earliest || start < earliest) earliest = start;
            if (!latest || end > latest) latest = end;
          });

          if (earliest && latest) {
            return { start: earliest, end: latest };
          }
        }
      } catch {
        // fall through to fallback
      }
    }

    // Fallback to system timestamps.
    const fallbackStart =
      parseDateTimeString(report.activityCreatedAt) ||
      new Date(report.activityCreatedAt);
    const fallbackEnd = report.activityCompletedAt
      ? parseDateTimeString(report.activityCompletedAt) ||
        new Date(report.activityCompletedAt)
      : fallbackStart;
    return { start: fallbackStart, end: fallbackEnd };
  };

  const formatDurationHours = (hours: number): number => {
    return Math.round(hours * 100) / 100;
  };

  const calculateBreakHoursFromRanges = (
    breaks: Array<{ start: string; end: string }>,
    rangeStart?: string,
    rangeEnd?: string,
  ): number => {
    if (!breaks || breaks.length === 0) return 0;
    const activityStart = parseDateTimeString(rangeStart);
    const activityEnd = parseDateTimeString(rangeEnd);

    const intervals = breaks
      .map((entry) => {
        const start = parseDateTimeString(entry.start);
        const end = parseDateTimeString(entry.end);
        if (!start || !end || end <= start) return null;
        let adjustedStart = start;
        let adjustedEnd = end;
        if (activityStart && adjustedStart < activityStart) {
          adjustedStart = activityStart;
        }
        if (activityEnd && adjustedEnd > activityEnd) {
          adjustedEnd = activityEnd;
        }
        if (adjustedEnd <= adjustedStart) return null;
        return { start: adjustedStart, end: adjustedEnd };
      })
      .filter(Boolean) as Array<{ start: Date; end: Date }>;

    if (intervals.length === 0) return 0;
    intervals.sort((a, b) => a.start.getTime() - b.start.getTime());
    let totalMs = 0;
    let current = intervals[0];
    for (let i = 1; i < intervals.length; i += 1) {
      const next = intervals[i];
      if (next.start <= current.end) {
        if (next.end > current.end) {
          current.end = next.end;
        }
      } else {
        totalMs += current.end.getTime() - current.start.getTime();
        current = next;
      }
    }
    totalMs += current.end.getTime() - current.start.getTime();
    return totalMs / (1000 * 60 * 60);
  };

  const getDurationFromUserDurationEntry = (entry: any): number => {
    if (typeof entry === "number" && Number.isFinite(entry) && entry > 0) {
      return entry;
    }

    if (!entry || typeof entry !== "object") {
      return 0;
    }

    const breakHours = Array.isArray(entry.breaks)
      ? calculateBreakHoursFromRanges(
          entry.breaks,
          entry.startTime,
          entry.endTime,
        )
      : 0;

    let duration =
      typeof entry.duration === "number" && Number.isFinite(entry.duration)
        ? entry.duration
        : 0;

    if (entry.startTime && entry.endTime) {
      const start = parseDateTimeString(entry.startTime);
      const end = parseDateTimeString(entry.endTime);
      if (start && end) {
        const diffMs = end.getTime() - start.getTime();
        if (diffMs > 0) {
          const rawHours = diffMs / (1000 * 60 * 60);
          duration = formatDurationHours(Math.max(rawHours - breakHours, 0));
        }
      }
    } else if (breakHours > 0) {
      duration = formatDurationHours(Math.max(duration - breakHours, 0));
    }

    return duration > 0 ? duration : 0;
  };

  const getWorkedSlicesFromUserDurationEntry = (
    entry: any,
  ): Array<{ at: Date; hours: number }> => {
    if (!entry || typeof entry !== "object") {
      return [];
    }

    const rawStart = parseDateTimeString(entry.startTime);
    const rawEnd = parseDateTimeString(entry.endTime);
    if (!rawStart || !rawEnd) {
      return [];
    }

    const workStart = rawStart;
    const workEnd =
      rawEnd <= rawStart
        ? new Date(rawEnd.getTime() + 24 * 60 * 60 * 1000)
        : rawEnd;
    if (workEnd <= workStart) {
      return [];
    }

    type Interval = { start: Date; end: Date };
    let intervals: Interval[] = [{ start: workStart, end: workEnd }];

    if (Array.isArray(entry.breaks) && entry.breaks.length > 0) {
      entry.breaks.forEach((breakEntry: any) => {
        const breakRawStart = parseDateTimeString(breakEntry?.start);
        const breakRawEnd = parseDateTimeString(breakEntry?.end);
        if (!breakRawStart || !breakRawEnd) {
          return;
        }

        const breakStart = breakRawStart;
        const breakEnd =
          breakRawEnd <= breakRawStart
            ? new Date(breakRawEnd.getTime() + 24 * 60 * 60 * 1000)
            : breakRawEnd;
        if (breakEnd <= breakStart) {
          return;
        }

        const nextIntervals: Interval[] = [];
        intervals.forEach((interval) => {
          if (breakEnd <= interval.start || breakStart >= interval.end) {
            nextIntervals.push(interval);
            return;
          }

          const cutStart =
            breakStart > interval.start ? breakStart : interval.start;
          const cutEnd = breakEnd < interval.end ? breakEnd : interval.end;

          if (cutStart > interval.start) {
            nextIntervals.push({ start: interval.start, end: cutStart });
          }
          if (cutEnd < interval.end) {
            nextIntervals.push({ start: cutEnd, end: interval.end });
          }
        });

        intervals = nextIntervals;
      });
    }

    const slices: Array<{ at: Date; hours: number }> = [];
    intervals.forEach((interval) => {
      let cursor = interval.start;
      while (cursor < interval.end) {
        const nextDay = new Date(
          cursor.getFullYear(),
          cursor.getMonth(),
          cursor.getDate() + 1,
          0,
          0,
          0,
          0,
        );
        const sliceEnd = nextDay < interval.end ? nextDay : interval.end;
        const hours =
          (sliceEnd.getTime() - cursor.getTime()) / (1000 * 60 * 60);
        if (hours > 0) {
          slices.push({
            at: cursor,
            hours: formatDurationHours(hours),
          });
        }
        cursor = sliceEnd;
      }
    });

    return slices;
  };

  const getReportSubmitDescription = (report: ActivityReport): string => {
    const raw = report.submitDescription;
    if (!raw || !raw.trim()) return "-";
    const trimmed = raw.trim();

    try {
      if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const assigneeId = report.assigneeId;
          const match =
            typeof assigneeId === "number"
              ? parsed.find(
                  (e) => String((e as any)?.userId) === String(assigneeId),
                )
              : parsed[0];
          const desc = (match as any)?.description;
          if (typeof desc === "string" && desc.trim()) {
            return desc.trim();
          }
          return "-";
        }

        if (parsed && typeof parsed === "object") {
          const candidates = [
            (parsed as any).description,
            (parsed as any).submitDescription,
            (parsed as any).comment,
            (parsed as any).note,
          ];
          const desc = candidates.find(
            (item) => typeof item === "string" && item.trim(),
          );
          return typeof desc === "string" ? desc.trim() : "-";
        }
      }
    } catch {
      // fall back to raw
    }

    return trimmed;
  };

  const parseSubmissionData = (data?: string | object) => {
    if (!data) return {};
    if (typeof data === "object") return data as Record<string, any>;
    try {
      return JSON.parse(data) as Record<string, any>;
    } catch {
      return {};
    }
  };

  const parseFormStructure = (data?: string | object) => {
    if (!data) return null;
    if (typeof data === "object") return data as any;
    try {
      return JSON.parse(data) as any;
    } catch {
      return null;
    }
  };

  const getFieldLabelFromStructure = (
    fieldId: string,
    formStructure: any,
  ): string => {
    if (!formStructure) {
      return fieldId
        .replace(/_/g, " ")
        .replace(/\b\w/g, (l) => l.toUpperCase());
    }

    if (formStructure.fields && Array.isArray(formStructure.fields)) {
      const field = formStructure.fields.find((f: any) => f.id === fieldId);
      if (field?.label) return String(field.label);
    }

    if (formStructure.elements && Array.isArray(formStructure.elements)) {
      const element = formStructure.elements.find(
        (el: any) => el.id === fieldId,
      );
      if (element?.label) return String(element.label);
    }

    return fieldId.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const formatSubmissionValue = (value: any): string => {
    if (value === null || value === undefined) return "";
    if (typeof value === "string") return value.trim();
    if (typeof value === "number" || typeof value === "boolean")
      return String(value);
    if (Array.isArray(value)) {
      const parts = value
        .map((v) => {
          if (v === null || v === undefined) return "";
          if (typeof v === "string") return v.trim();
          if (typeof v === "number" || typeof v === "boolean") return String(v);
          if (typeof v === "object") {
            if ("label" in (v as any)) return String((v as any).label);
            if ("name" in (v as any)) return String((v as any).name);
          }
          try {
            return JSON.stringify(v);
          } catch {
            return "";
          }
        })
        .filter((s) => s);
      return parts.join(", ");
    }
    if (typeof value === "object") {
      if ("label" in value) return String((value as any).label);
      if ("name" in value) return String((value as any).name);
      try {
        const str = JSON.stringify(value);
        return str.length > 120 ? `${str.slice(0, 117)}...` : str;
      } catch {
        return "";
      }
    }
    return String(value);
  };

  const getReportFormContent = (
    report: ActivityReport,
  ): { summary: string; tooltip: string } => {
    if (
      (!report.formSubmissions || report.formSubmissions.length === 0) &&
      (report.formContentSummary || report.formContentTooltip)
    ) {
      return {
        summary: report.formContentSummary || "-",
        tooltip: report.formContentTooltip || "",
      };
    }

    const forms = Array.isArray(report.formSubmissions)
      ? report.formSubmissions
      : [];
    if (forms.length === 0) {
      return { summary: "-", tooltip: "" };
    }

    // Prefer latest submitted form if possible.
    const form: any = [...forms].sort((a: any, b: any) => {
      const aTime = a?.submittedAt ? new Date(a.submittedAt).getTime() : 0;
      const bTime = b?.submittedAt ? new Date(b.submittedAt).getTime() : 0;
      return bTime - aTime;
    })[0];

    const submissionData = parseSubmissionData(form?.submissionData);
    const formStructure = parseFormStructure(form?.formStructure);

    const pairs = Object.entries(submissionData)
      .map(([key, rawValue]) => {
        const label = getFieldLabelFromStructure(key, formStructure);
        const value = formatSubmissionValue(rawValue);
        if (!value) return null;
        return { label, value };
      })
      .filter(Boolean) as Array<{ label: string; value: string }>;

    if (pairs.length === 0) {
      const meta =
        forms.length > 1
          ? `${forms.length} form`
          : form?.formName
            ? String(form.formName)
            : "";
      return { summary: meta || "-", tooltip: "" };
    }

    const full = pairs.map((p) => `${p.label}: ${p.value}`);
    const summaryParts = full.slice(0, 3);
    const summary =
      full.length > 3
        ? `${summaryParts.join(" | ")}  +${full.length - 3}`
        : summaryParts.join(" | ");
    return {
      summary,
      tooltip: full.join("\n"),
    };
  };

  const getReportLeaveType = useCallback(
    (report: ActivityReport): { summary: string; tooltip: string } => {
      const content = getReportFormContent(report);
      const targetLabel = normalizeActivityTableAsciiLabel(
        LEAVE_TYPE_FIELD_LABEL,
      );
      const values = parseFormFieldPairs(content.tooltip)
        .filter(
          (pair) =>
            normalizeActivityTableAsciiLabel(pair.label) === targetLabel,
        )
        .map((pair) => pair.value.trim())
        .filter(Boolean);

      const combined = Array.from(new Set(values)).join(" | ");

      if (!combined) {
        return {
          summary: content.summary || "-",
          tooltip: content.tooltip || content.summary || "",
        };
      }

      return {
        summary: combined,
        tooltip: combined,
      };
    },
    [getReportFormContent],
  );

  const activityDefaultColumns = useMemo<
    ActivityTableColumnPreference[]
  >(() => {
    const columns: ActivityTableColumnPreference[] = [
      { id: "rowNo", kind: "default", label: "Sıra No" },
      { id: "endDate", kind: "default", label: "Bitiş Tarihi" },
      { id: "assignee", kind: "default", label: "Çalışan Kişi" },
      { id: "startTime", kind: "default", label: "Başlangıç Saati" },
      { id: "endTimeOnly", kind: "default", label: "Bitiş Saati" },
      { id: "duration", kind: "default", label: "Çalışılan Süre" },
      { id: "projectName", kind: "default", label: "Çalışılan Proje" },
      { id: "activityTitle", kind: "default", label: "Yapılan Aktivite" },
      { id: "formContent", kind: "default", label: "Form İçeriği" },
      {
        id: "submitDescription",
        kind: "default",
        label: activitySubmitDescriptionLabel,
      },
      { id: "managerComment", kind: "default", label: "Yönetici Yorumu" },
      { id: "details", kind: "default", label: t("common:details") },
    ];

    if (canViewCosts) {
      columns.splice(6, 0, {
        id: "cost",
        kind: "default",
        label: "Maliyet (TL)",
      });
    }

    const translatedDefaultLabels: Record<string, string> = {
      rowNo: t("dashboard:managerActivities.rowNo"),
      endDate: isCompanyActivitiesVariant
        ? t("dashboard:date")
        : t("dashboard:managerActivities.endDate"),
      assignee: t("dashboard:managerActivities.employeeName"),
      startTime: t("dashboard:managerActivities.startTime"),
      endTimeOnly: t("dashboard:managerActivities.endTime"),
      duration: t("dashboard:managerActivities.duration"),
      cost: t("dashboard:managerActivities.costTl"),
      projectName: t("dashboard:managerActivities.projectName"),
      activityTitle: t("dashboard:managerActivities.activityTitle"),
      formContent: t("dashboard:managerActivities.formContent"),
      managerComment: t("dashboard:managerActivities.managerComment"),
    };

    columns.forEach((column) => {
      if (column.kind !== "default") {
        return;
      }
      const translatedLabel = translatedDefaultLabels[column.id];
      if (translatedLabel) {
        column.label = translatedLabel;
      }
    });

    return columns;
  }, [activitySubmitDescriptionLabel, canViewCosts, isCompanyActivitiesVariant, t]);

  const storedActivityTableLayout = useMemo<
    ActivityTableColumnPreference[]
  >(() => {
    const raw =
      companySettings?.activityTableLayouts?.[
        MANAGER_ACTIVITY_TABLE_SCREEN_KEY
      ];
    if (!Array.isArray(raw)) {
      return [];
    }

    return dedupeActivityTableColumns(
      raw
        .map((item) => {
          if (!item || typeof item !== "object") {
            return null;
          }
          const id =
            typeof (item as any).id === "string" ? (item as any).id.trim() : "";
          const kind =
            (item as any).kind === "form"
              ? "form"
              : (item as any).kind === "default"
                ? "default"
                : null;
          const label =
            typeof (item as any).label === "string"
              ? sanitizeActivityTableLabel((item as any).label.trim())
              : "";
          if (!id || !kind || !label) {
            return null;
          }
          return {
            id,
            kind,
            label,
          } satisfies ActivityTableColumnPreference;
        })
        .filter((item): item is ActivityTableColumnPreference => item !== null),
    );
  }, [companySettings]);

  const activityAvailableFormColumns = useMemo(
    () =>
      collectFormColumnPreferences(
        reports
          .filter((report) => !isLeaveActivityReport(report))
          .map((report) => getReportFormContent(report).tooltip),
      ),
    [getReportFormContent, isLeaveActivityReport, reports],
  );

  const activityVisibleColumns = useMemo<
    ActivityTableColumnPreference[]
  >(() => {
    if (!storedActivityTableLayout.length) {
      return activityDefaultColumns;
    }

    const defaultMap = new Map(
      activityDefaultColumns.map((column) => [column.id, column]),
    );
    const formMap = new Map(
      activityAvailableFormColumns.map((column) => [column.id, column]),
    );

    const resolved = storedActivityTableLayout
      .map((column) => {
        if (column.kind === "default") {
          return defaultMap.get(column.id) || null;
        }
        return formMap.get(column.id) || null;
      })
      .filter(
        (column): column is ActivityTableColumnPreference => column !== null,
      );

    return resolved.length
      ? dedupeActivityTableColumns(resolved)
      : activityDefaultColumns;
  }, [
    activityAvailableFormColumns,
    activityDefaultColumns,
    storedActivityTableLayout,
  ]);

  const activityColumnSortFieldById: Record<string, string | null> = {
    rowNo: null,
    endDate: "completedAt",
    assignee: "assignedTo",
    startTime: "activityCreatedAt",
    endTimeOnly: "completedAt",
    duration: "durationHours",
    cost: "costAmount",
    projectName: "projectName",
    activityTitle: "activityTitle",
    formContent: null,
    submitDescription: null,
    managerComment: "managerComment",
    details: null,
  };

  const persistActivityTableLayout = async (
    columns: ActivityTableColumnPreference[],
  ) => {
    try {
      setIsSavingActivityTableLayout(true);
      const response = await apiService.updateActivityTableLayout(
        MANAGER_ACTIVITY_TABLE_SCREEN_KEY,
        columns,
      );
      const settings = (response?.data || response) as Record<string, any>;
      applyCompanySettings(settings);
      setIsActivityTableConfiguratorOpen(false);
      toast.success(t("dashboard:managerActivities.layoutSaved"));
    } catch (error) {
      toast.error(t("errors:generic"));
    } finally {
      setIsSavingActivityTableLayout(false);
    }
  };

  // Parse user durations to get TOTAL time data (sum of all users)
  const getUserTimeInfo = (
    userDurations: string | undefined,
  ): {
    startTime: string;
    endTime: string;
    duration: number;
    userCount: number;
  } | null => {
    if (!userDurations) return null;
    try {
      const parsed = JSON.parse(userDurations);
      const userIds = Object.keys(parsed);
      if (userIds.length === 0) return null;

      // Calculate total duration from all users
      let totalDuration = 0;
      let earliestStart = "";
      let latestEnd = "";

      userIds.forEach((userId) => {
        const userData = parsed[userId];

        // New format: { startTime, endTime, duration }
        if (
          userData &&
          typeof userData === "object" &&
          "duration" in userData
        ) {
          // Recalculate duration from startTime and endTime for accuracy
          const breakHours = Array.isArray(userData.breaks)
            ? calculateBreakHoursFromRanges(
                userData.breaks,
                userData.startTime,
                userData.endTime,
              )
            : 0;
          let userDuration = userData.duration || 0;
          if (userData.startTime && userData.endTime) {
            const start = parseDateTimeString(userData.startTime);
            const end = parseDateTimeString(userData.endTime);
            if (start && end) {
              const diffMs = end.getTime() - start.getTime();
              if (diffMs > 0) {
                const rawHours = diffMs / (1000 * 60 * 60);
                userDuration = formatDurationHours(
                  Math.max(rawHours - breakHours, 0),
                );
              }
            }
          } else if (breakHours > 0) {
            userDuration = formatDurationHours(
              Math.max(userDuration - breakHours, 0),
            );
          }
          totalDuration += userDuration;

          // Track earliest start and latest end across all users (for multi-user activities, this gives the widest range)
          if (userData.startTime) {
            if (!earliestStart || userData.startTime < earliestStart) {
              earliestStart = userData.startTime;
            }
          }
          if (userData.endTime) {
            if (!latestEnd || userData.endTime > latestEnd) {
              latestEnd = userData.endTime;
            }
          }
        }
        // Legacy format: just a number
        else if (typeof userData === "number") {
          totalDuration += userData;
        }
      });

      return {
        startTime: earliestStart,
        endTime: latestEnd,
        duration: totalDuration,
        userCount: userIds.length,
      };
    } catch (error) {
      return null;
    }
  };

  const getReportDuration = (report: ActivityReport): number => {
    const timeInfo = getUserTimeInfo(report.userDurations);
    if (timeInfo && timeInfo.duration > 0) {
      return timeInfo.duration;
    }
    if (report.duration && report.duration > 0) {
      return report.duration;
    }
    return 0;
  };

  const { activityCostByActivityId, projectRealizedCostByWorkflowId } =
      useMemo(() => {
        const activityCosts = new Map<number, number>();
        const projectCostsByWorkflowId = new Map<number, number>();

      if (!canViewCosts) {
        return {
          activityCostByActivityId: activityCosts,
          projectRealizedCostByWorkflowId: projectCostsByWorkflowId,
        };
      }

        const costSegments: OvertimeCostSegment[] = [];

        effectiveActivityReports.forEach((report) => {
          if (isLeaveActivityReport(report)) {
            return;
          }

          const { start, end } = getReportStartEnd(report);
          const fallbackDate = end || start || null;
          let hasAnyUserDuration = false;

          if (report.userDurations) {
            try {
              const parsed = JSON.parse(report.userDurations);
            if (parsed && typeof parsed === "object") {
              Object.entries(parsed).forEach(([userIdRaw, entry]) => {
                const userId = Number(userIdRaw);
                if (!Number.isFinite(userId)) return;

                  const workedSlices =
                    getWorkedSlicesFromUserDurationEntry(entry);
                  if (workedSlices.length > 0) {
                    hasAnyUserDuration = true;
                    workedSlices.forEach((slice) => {
                      if (!Number.isFinite(slice.hours) || slice.hours <= 0) {
                        return;
                      }
                      costSegments.push({
                        activityId: report.activityId,
                        userId,
                        bucketKey: `user:${userId}`,
                        at: slice.at,
                        hours: slice.hours,
                        rate: resolveApplicableHourlyRate(userId, slice.at),
                      });
                    });
                    return;
                  }

                  const userDuration = getDurationFromUserDurationEntry(entry);
                if (userDuration <= 0) return;

                hasAnyUserDuration = true;
                  const entryDate =
                    typeof entry === "object" && entry
                      ? parseDateTimeString((entry as any).endTime) ||
                        parseDateTimeString((entry as any).startTime) ||
                        fallbackDate
                      : fallbackDate;
                  costSegments.push({
                    activityId: report.activityId,
                    userId,
                    bucketKey: `user:${userId}`,
                    at: entryDate,
                    hours: userDuration,
                    rate: resolveApplicableHourlyRate(userId, entryDate),
                  });
                });
              }
            } catch {
              // ignore malformed userDurations
            }
        }

          if (!hasAnyUserDuration) {
            const duration = getReportDuration(report);
            if (duration > 0) {
              costSegments.push({
                activityId: report.activityId,
                userId: report.assigneeId,
                bucketKey:
                  typeof report.assigneeId === "number"
                    ? `user:${report.assigneeId}`
                    : `activity:${report.activityId}`,
                at: fallbackDate,
                hours: duration,
                rate: resolveApplicableHourlyRate(
                  report.assigneeId,
                  fallbackDate,
                ),
              });
            }
          }
        });

        const allocation = allocateOvertimeCosts({
          segments: costSegments,
          overtimeSettings,
        });

        allocation.costByActivityId.forEach((value, activityId) => {
          activityCosts.set(activityId, Number.isFinite(value) ? value : 0);
        });

        effectiveActivityReports.forEach((report) => {
          if (report.activityStatus !== "COMPLETED") {
            return;
          }
          const safeCost = activityCosts.get(report.activityId) ?? 0;

          if (
            typeof report.workflowId === "number" &&
            Number.isFinite(report.workflowId)
          ) {
          projectCostsByWorkflowId.set(
            report.workflowId,
            (projectCostsByWorkflowId.get(report.workflowId) ?? 0) + safeCost,
          );
        }
      });

      return {
        activityCostByActivityId: activityCosts,
        projectRealizedCostByWorkflowId: projectCostsByWorkflowId,
      };
    }, [
        canViewCosts,
        getReportDuration,
        getReportStartEnd,
        getWorkedSlicesFromUserDurationEntry,
        isLeaveActivityReport,
        overtimeSettings,
        parseDateTimeString,
        effectiveActivityReports,
        resolveApplicableHourlyRate,
        getDurationFromUserDurationEntry,
      ]);

  const getProjectCode = (
    project: WorkflowInstanceItem,
    formData?: Record<string, any>,
  ): string => {
    if (project.projectCode) {
      return project.projectCode;
    }

    return buildProjectCode({
      projectTypeName:
        project.templateWorkflowName || project.name || formData?.projectType,
      startDate: project.createdAt,
    });
  };

  const projectsModel = useProjectsTabModel({
    mainTab,
    projects,
    projectCollections,
    users,
    canViewCosts,
    workflowText,
    t,
    formatHoursTr,
    getPlannedHours,
    getProjectFinanceSummaryForDashboard,
    getProjectCode,
    getApplicableAverageRate,
    projectRealizedCostByWorkflowId,
    getProjectCurrencyForFormData,
    convertAmountToTry,
    convertTryAmountToCurrency,
  });

  const projectsTabLoading = reportsLoading || projectsLoading;

  const reportsModel = useReportsTabModel({
    mainTab,
    activityDefinitions,
    users,
    projects,
    reports,
    canViewCosts,
    fetchActivities,
    fetchUsers,
    formatCurrencyTr,
    getReportDuration,
    getReportStartEnd,
    getDurationFromUserDurationEntry,
    getWorkedSlicesFromUserDurationEntry,
    resolveApplicableHourlyRate,
    isLeaveActivityReport,
    overtimeSettings,
    t,
  });

  const activityTabReports = useMemo(() => {
    if (mainTab === "leaves") {
      return filteredReports.filter(
        (r) =>
          isLeaveActivityReport(r) &&
          (r.activityStatus === "COMPLETED" ||
            r.activityStatus === "PENDING_APPROVAL"),
      );
    }
    if (isActivityLikeTab) {
      return filteredReports.filter((r) => !isLeaveActivityReport(r));
    }
    return [];
  }, [filteredReports, isActivityLikeTab, mainTab, isLeaveActivityReport]);

  const isShowAllActivities = activityItemsPerPage <= 0;
  const activityPageStartIndex = useMemo(
    () =>
      isShowAllActivities
        ? 0
        : (activityCurrentPage - 1) * activityItemsPerPage,
    [activityCurrentPage, activityItemsPerPage, isShowAllActivities],
  );

  const activityTotalPages = useMemo(() => {
    if (isShowAllActivities) return 1;
    return Math.max(
      1,
      Math.ceil(activityTabReports.length / activityItemsPerPage),
    );
  }, [activityTabReports.length, activityItemsPerPage, isShowAllActivities]);
  const paginatedReports = useMemo(() => {
    if (isShowAllActivities) return activityTabReports;
    return activityTabReports.slice(
      activityPageStartIndex,
      activityPageStartIndex + activityItemsPerPage,
    );
  }, [
    activityTabReports,
    activityPageStartIndex,
    activityItemsPerPage,
    isShowAllActivities,
  ]);
  const filteredActivitiesTotalDuration = useMemo(() => {
    if (!isActivityLikeTab) {
      return 0;
    }
    return activityTabReports.reduce((sum, report) => {
      const duration = getReportDuration(report);
      return sum + (Number.isFinite(duration) && duration > 0 ? duration : 0);
    }, 0);
  }, [activityTabReports, isActivityLikeTab]);
  const filteredActivitiesAverageDuration = useMemo(() => {
    if (!isActivityLikeTab || activityTabReports.length === 0) {
      return 0;
    }
    return filteredActivitiesTotalDuration / activityTabReports.length;
  }, [
    activityTabReports.length,
    filteredActivitiesTotalDuration,
    isActivityLikeTab,
  ]);
  const filteredLeavesTotalDuration = useMemo(() => {
    if (mainTab !== "leaves") {
      return 0;
    }
    return activityTabReports.reduce((sum, report) => {
      const duration = getReportDuration(report);
      return sum + (Number.isFinite(duration) && duration > 0 ? duration : 0);
    }, 0);
  }, [activityTabReports, mainTab]);
  const filteredLeavesAverageDuration = useMemo(() => {
    if (mainTab !== "leaves" || activityTabReports.length === 0) {
      return 0;
    }
    return filteredLeavesTotalDuration / activityTabReports.length;
  }, [activityTabReports.length, filteredLeavesTotalDuration, mainTab]);
  const filteredActivitiesTotalCost = useMemo(() => {
    if (!canViewCosts || !isActivityLikeTab) {
      return 0;
    }
    return activityTabReports.reduce((sum, report) => {
      const value = activityCostByActivityId.get(report.activityId) ?? 0;
      return sum + (Number.isFinite(value) ? value : 0);
    }, 0);
  }, [
    activityCostByActivityId,
    activityTabReports,
    canViewCosts,
    isActivityLikeTab,
  ]);
  const filteredActivitiesAverageCost = useMemo(() => {
    if (
      !canViewCosts ||
      !isActivityLikeTab ||
      activityTabReports.length === 0
    ) {
      return 0;
    }
    return filteredActivitiesTotalCost / activityTabReports.length;
  }, [
    activityTabReports.length,
    canViewCosts,
    filteredActivitiesTotalCost,
    isActivityLikeTab,
  ]);
  const pageRootRef = useRef<HTMLDivElement | null>(null);
  const [pageViewportHeight, setPageViewportHeight] = useState<number | null>(
    null,
  );

  const syncPageViewportHeight = useCallback(() => {
    const pageRoot = pageRootRef.current;
    if (!pageRoot) {
      return;
    }

    const appScaleRaw = getComputedStyle(document.documentElement)
      .getPropertyValue("--app-ui-scale")
      .trim();
    const parsedScale = Number.parseFloat(appScaleRaw);
    const appScale =
      Number.isFinite(parsedScale) && parsedScale > 0 ? parsedScale : 1;
    const viewportHeight = window.innerHeight / appScale;
    const pageTop = Math.max(pageRoot.getBoundingClientRect().top, 0);
    const availableHeight = Math.floor(viewportHeight - pageTop);
    setPageViewportHeight(Math.max(320, availableHeight));
  }, []);
  useEffect(() => {
    const frameId = window.requestAnimationFrame(syncPageViewportHeight);
    window.addEventListener("resize", syncPageViewportHeight);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener("resize", syncPageViewportHeight);
    };
  }, [syncPageViewportHeight]);

  const tableCardRef = useRef<HTMLDivElement | null>(null);
  const [tableCardMaxHeight, setTableCardMaxHeight] = useState<number | null>(
    null,
  );
  const syncTableCardMaxHeight = useCallback(() => {
    const tableCard = tableCardRef.current;
    if (!tableCard) {
      return;
    }

    const appScaleRaw = getComputedStyle(document.documentElement)
      .getPropertyValue("--app-ui-scale")
      .trim();
    const parsedScale = Number.parseFloat(appScaleRaw);
    const appScale =
      Number.isFinite(parsedScale) && parsedScale > 0 ? parsedScale : 1;
    const viewportHeight = window.innerHeight / appScale;
    const cardTop = Math.max(tableCard.getBoundingClientRect().top, 0);
    const availableHeight = Math.floor(viewportHeight - cardTop - 24);
    setTableCardMaxHeight(Math.max(220, availableHeight));
  }, []);
  useEffect(() => {
    const frameId = window.requestAnimationFrame(syncTableCardMaxHeight);
    window.addEventListener("resize", syncTableCardMaxHeight);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener("resize", syncTableCardMaxHeight);
    };
  }, [
    syncTableCardMaxHeight,
    mainTab,
    variant,
    loading,
    projects.length,
    activityTabReports.length,
    reportsModel.displayMonthlyReport.length,
  ]);
  const tableCardStyle = tableCardMaxHeight
    ? { maxHeight: `${tableCardMaxHeight}px` }
    : undefined;

  const latestManagerCommentRequestKey = useMemo(() => {
    const ids = paginatedReports
      .map((r) => r.activityId)
      .filter(
        (id): id is number => typeof id === "number" && Number.isFinite(id),
      );
    return ids.sort((a, b) => a - b).join(",");
  }, [paginatedReports]);

  useEffect(() => {
    const loadLatest = async () => {
      try {
        if (!latestManagerCommentRequestKey) {
          setLatestManagerCommentsByActivityId({});
          return;
        }

        const uniqueIds = latestManagerCommentRequestKey
          .split(",")
          .map((value) => Number(value))
          .filter((id) => Number.isFinite(id));

        const resp = await apiService.getLatestActivityManagerComments({
          activityInstanceIds: uniqueIds,
        });
        const data = resp?.data || resp;
        const map = (data?.activities || {}) as Record<
          string,
          LatestManagerCommentRow
        >;
        setLatestManagerCommentsByActivityId(map || {});
      } catch (error) {
        setLatestManagerCommentsByActivityId({});
      }
    };

    void loadLatest();
  }, [latestManagerCommentRequestKey]);

  const handleApproveActivity = async (activityInstanceId: number) => {
    try {
      await apiService.approveActivityInstance(activityInstanceId);
      toast.success(t("common:approve"));
      await refreshReports();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t("errors:generic"));
    }
  };

  const handleRejectActivity = async (activityInstanceId: number) => {
    try {
      await apiService.rejectActivityInstance(activityInstanceId);
      toast.success(t("common:reject"));
      await refreshReports();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t("errors:generic"));
    }
  };

  const handleViewDetails = async (report: ActivityReport) => {
    setSelectedTask(report);
    setIsModalOpen(true);
    try {
      const detailedReport = await apiService.getActivityReportDetail(
        report.activityId,
      );
      setSelectedTask(detailedReport);
    } catch (error) {
    }
  };

  const handleOpenDeleteActivityConfirm = (report: ActivityReport) => {
    setActivityReportToDelete(report);
    setShowDeleteActivityConfirm(true);
  };

  const handleCancelDeleteActivity = () => {
    if (deletingActivity) return;
    setShowDeleteActivityConfirm(false);
    setActivityReportToDelete(null);
  };

  const handleConfirmDeleteActivity = async () => {
    if (!activityReportToDelete) return;
    try {
      setDeletingActivity(true);
      await apiService.deleteActivityInstance(activityReportToDelete.activityId);
      toast.success(t("common:delete"));
      setShowDeleteActivityConfirm(false);
      setActivityReportToDelete(null);
      await refreshReports();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t("errors:generic"));
    } finally {
      setDeletingActivity(false);
    }
  };

  const handleViewProjectFiles = (
    project: WorkflowInstanceItem,
    formData?: Record<string, any>,
  ) => {
    setSelectedProjectForFiles({
      project,
      projectName:
        project.workflowContainerName ||
        project.name ||
        formData?.projectName ||
        "-",
      projectManagerId:
        (project as any).projectManagerId ||
        formData?.projectManagerId ||
        formData?.projectManager ||
        null,
    });
    setShowProjectFilesModal(true);
  };

  const handleCloseProjectFilesModal = () => {
    setShowProjectFilesModal(false);
    setSelectedProjectForFiles(null);
  };

  const handleEditProject = (project: WorkflowInstanceItem) => {
    setSelectedProjectForEdit(project);
    setShowProjectEditModal(true);
  };

  const handleCloseProjectEditModal = () => {
    setShowProjectEditModal(false);
    setSelectedProjectForEdit(null);
  };

  const openManagerCommentModal = (report: ActivityReport) => {
    if (!(isAdmin() || isManager())) return;
    const existing =
      latestManagerCommentsByActivityId[String(report.activityId)];
    setManagerCommentTarget({
      activityInstanceId: report.activityId,
      targetUserId: report.assigneeId ?? null,
      assigneeName: report.assigneeName || undefined,
      activityTitle: report.activityTitle || undefined,
      projectName: report.projectName || undefined,
    });
    setManagerCommentText((existing?.comment ?? "").toString());
  };

  const closeManagerCommentModal = () => {
    if (savingManagerComment) return;
    setManagerCommentTarget(null);
    setManagerCommentText("");
  };

  const saveManagerComment = async () => {
    if (!managerCommentTarget) return;
    if (!(isAdmin() || isManager())) return;

    const comment = managerCommentText.trim();
    if (!comment) {
      toast.error(t("dashboard:managerActivities.commentEmpty"));
      return;
    }

    try {
      setSavingManagerComment(true);
      const resp = await apiService.createActivityManagerComment({
        activityInstanceId: managerCommentTarget.activityInstanceId,
        targetUserId: managerCommentTarget.targetUserId ?? null,
        comment,
        isGlobal: true,
      });

      const createdAt =
        resp?.data?.createdAt ||
        resp?.data?.data?.createdAt ||
        new Date().toISOString();
      const authorName =
        resp?.data?.authorName ||
        resp?.data?.data?.authorName ||
        `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim() ||
        user?.username ||
        null;

      setLatestManagerCommentsByActivityId((prev) => ({
        ...prev,
        [String(managerCommentTarget.activityInstanceId)]: {
          id: Number(resp?.data?.id || resp?.data?.data?.id || 0),
          activityInstanceId: managerCommentTarget.activityInstanceId,
          targetUserId: managerCommentTarget.targetUserId ?? null,
          comment,
          authorName,
          createdAt,
        },
      }));
      toast.success(t("common:save"));
      closeManagerCommentModal();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t("errors:generic"));
    } finally {
      setSavingManagerComment(false);
    }
  };

  const renderActivityFilterCell = (column: ActivityTableColumnPreference) => {
    if (column.kind === "form") {
      return (
        <th key={`activity-filter-${column.id}`} className="px-3 py-1.5">
          <input
            type="text"
            value={activityFormColumnFilters[column.id] || ""}
            onChange={(e) =>
              setActivityFormColumnFilters((prev) => ({
                ...prev,
                [column.id]: e.target.value,
              }))
            }
            placeholder={column.label}
            className="w-full min-w-[140px] px-2 py-1 text-[11px] border border-blue-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </th>
      );
    }

    switch (column.id) {
      case "rowNo":
        return (
          <th
            key={`activity-filter-${column.id}`}
            className="px-3 py-1.5 sticky left-0 z-40 bg-blue-50/90 border-r border-blue-200"
          >
            <button
              type="button"
              onClick={clearActivityRowFilters}
              className="px-2 py-1 text-[10px] font-semibold rounded border border-blue-300 text-blue-700 hover:bg-blue-100"
              title="Satır filtrelerini temizle"
            >
              Temizle
            </button>
          </th>
        );
      case "endDate":
        return (
          <th key={`activity-filter-${column.id}`} className="px-3 py-1.5">
            <input
              type="date"
              value={activityRowFilters.endDate}
              onChange={(e) =>
                setActivityRowFilters((prev) => ({
                  ...prev,
                  endDate: e.target.value,
                }))
              }
              className="w-full min-w-[130px] px-2 py-1 text-[11px] border border-blue-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </th>
        );
      case "assignee":
        return (
          <th key={`activity-filter-${column.id}`} className="px-3 py-1.5">
            <input
              type="text"
              value={activityRowFilters.assignee}
              onChange={(e) =>
                setActivityRowFilters((prev) => ({
                  ...prev,
                  assignee: e.target.value,
                }))
              }
              placeholder="Çalışan"
              className="w-full min-w-[130px] px-2 py-1 text-[11px] border border-blue-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </th>
        );
      case "startTime":
        return (
          <th key={`activity-filter-${column.id}`} className="px-3 py-1.5">
            <input
              type="time"
              value={activityRowFilters.startTime}
              onChange={(e) =>
                setActivityRowFilters((prev) => ({
                  ...prev,
                  startTime: e.target.value,
                }))
              }
              className="w-full min-w-[95px] px-2 py-1 text-[11px] border border-blue-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </th>
        );
      case "endTimeOnly":
        return (
          <th key={`activity-filter-${column.id}`} className="px-3 py-1.5">
            <input
              type="time"
              value={activityRowFilters.endTime}
              onChange={(e) =>
                setActivityRowFilters((prev) => ({
                  ...prev,
                  endTime: e.target.value,
                }))
              }
              className="w-full min-w-[95px] px-2 py-1 text-[11px] border border-blue-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </th>
        );
      case "duration":
        return (
          <th
            key={`activity-filter-${column.id}`}
            className="px-3 py-1.5 text-right"
          >
            <input
              type="number"
              min="0"
              step="0.25"
              value={activityRowFilters.minDurationHours}
              onChange={(e) =>
                setActivityRowFilters((prev) => ({
                  ...prev,
                  minDurationHours: e.target.value,
                }))
              }
              placeholder={t("dashboard:managerActivities.minHoursPlaceholder")}
              className="w-full min-w-[90px] px-2 py-1 text-[11px] text-right border border-blue-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </th>
        );
      case "cost":
        return (
          <th
            key={`activity-filter-${column.id}`}
            className="px-3 py-1.5 text-right"
          >
            <input
              type="number"
              min="0"
              step="0.01"
              value={activityRowFilters.minCost}
              onChange={(e) =>
                setActivityRowFilters((prev) => ({
                  ...prev,
                  minCost: e.target.value,
                }))
              }
              placeholder={t("dashboard:managerActivities.minCostPlaceholder")}
              className="w-full min-w-[90px] px-2 py-1 text-[11px] text-right border border-blue-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </th>
        );
      case "projectName":
        return (
          <th key={`activity-filter-${column.id}`} className="px-3 py-1.5">
            <input
              type="text"
              value={activityRowFilters.projectName}
              onChange={(e) =>
                setActivityRowFilters((prev) => ({
                  ...prev,
                  projectName: e.target.value,
                }))
              }
              placeholder={t("dashboard:managerActivities.projectName")}
              className="w-full min-w-[130px] px-2 py-1 text-[11px] border border-blue-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </th>
        );
      case "activityTitle":
        return (
          <th key={`activity-filter-${column.id}`} className="px-3 py-1.5">
            <input
              type="text"
              value={activityRowFilters.activityTitle}
              onChange={(e) =>
                setActivityRowFilters((prev) => ({
                  ...prev,
                  activityTitle: e.target.value,
                }))
              }
              placeholder={t("dashboard:managerActivities.activityTitle")}
              className="w-full min-w-[130px] px-2 py-1 text-[11px] border border-blue-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </th>
        );
      case "formContent":
        return (
          <th key={`activity-filter-${column.id}`} className="px-3 py-1.5">
            <input
              type="text"
              value={activityRowFilters.formContent}
              onChange={(e) =>
                setActivityRowFilters((prev) => ({
                  ...prev,
                  formContent: e.target.value,
                }))
              }
              placeholder={t("dashboard:managerActivities.formContent")}
              className="w-full min-w-[160px] px-2 py-1 text-[11px] border border-blue-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </th>
        );
      case "submitDescription":
        return (
          <th key={`activity-filter-${column.id}`} className="px-3 py-1.5">
            <input
              type="text"
              value={activityRowFilters.description}
              onChange={(e) =>
                setActivityRowFilters((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              placeholder={activitySubmitDescriptionLabel}
              className="w-full min-w-[130px] px-2 py-1 text-[11px] border border-blue-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </th>
        );
      case "managerComment":
        return (
          <th key={`activity-filter-${column.id}`} className="px-3 py-1.5">
            <input
              type="text"
              value={activityRowFilters.managerComment}
              onChange={(e) =>
                setActivityRowFilters((prev) => ({
                  ...prev,
                  managerComment: e.target.value,
                }))
              }
              placeholder={t("dashboard:managerActivities.managerComment")}
              className="w-full min-w-[160px] px-2 py-1 text-[11px] border border-blue-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </th>
        );
      default:
        return (
          <th
            key={`activity-filter-${column.id}`}
            className="px-3 py-1.5 text-[11px] text-blue-700 font-semibold"
          >
            -
          </th>
        );
    }
  };

  useEffect(() => {
    if (variant === "company-activities" && mainTab !== "activities") {
      setMainTab("activities");
    }
  }, [variant, mainTab]);

  useEffect(() => {
    if (
      variant === "manager" &&
      requestedMainTab &&
      appliedLocationTabKeyRef.current !== location.key
    ) {
      appliedLocationTabKeyRef.current = location.key;
      setMainTab(requestedMainTab);
    }
  }, [location.key, requestedMainTab, variant]);

  useEffect(() => {
    if (variant === "company-activities" && statusFilter === "PENDING_APPROVAL") {
      setStatusFilter("all");
    }
  }, [statusFilter, variant, setStatusFilter]);

  return (
    <div
      ref={pageRootRef}
      className="bg-gradient-to-br from-slate-50 via-purple-50/30 to-blue-50 pt-8 pb-0 box-border overflow-hidden"
      style={{
        height: pageViewportHeight
          ? `${pageViewportHeight}px`
          : "calc(100vh / var(--app-ui-scale, 1))",
      }}
    >
      <div className="max-w-full px-4 sm:px-6 lg:px-8 h-full flex flex-col min-h-0">
        <ManagerDashboardHeader
          variant={variant}
          mainTab={mainTab}
          onTabChange={setMainTab}
          showAssignTaskButton={
            (isAdmin() || isManager()) &&
            (mainTab === "activities" || mainTab === "tasks")
          }
          onAssignTask={() => setShowAssignTaskModal(true)}
        />

        {isActivityLikeTab && (
          <ActivitiesTab
            isTasksTab={mainTab === "tasks"}
            showPendingApprovalStatusOption={
              !isCompanyActivitiesVariant
            }
            useSingleDateFilter={isCompanyActivitiesVariant}
            loading={loading}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            completedCount={completedCount}
            pendingApprovalCount={pendingApprovalCount}
            pendingCount={pendingCount}
            rejectedCount={rejectedCount}
            taskActivitiesCount={taskActivitiesCount}
            taskActivitiesFilter={TASK_ACTIVITIES_FILTER}
            workflowText={workflowText}
            workflowFilter={workflowFilter}
            setWorkflowFilter={setWorkflowFilter}
            uniqueWorkflows={uniqueWorkflows}
            selectedProject={selectedProject}
            setSelectedProject={setSelectedProject}
            uniqueProjects={uniqueProjects}
            activityFilter={activityFilter}
            setActivityFilter={setActivityFilter}
            uniqueActivities={uniqueActivities}
            assigneeFilter={assigneeFilter}
            setAssigneeFilter={setAssigneeFilter}
            uniqueAssignees={uniqueAssignees}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            hasActiveFilters={hasActiveFilters}
            clearAllFilters={clearAllFilters}
            tableCardRef={tableCardRef}
            tableCardStyle={tableCardStyle}
            isActivityTableConfiguratorOpen={isActivityTableConfiguratorOpen}
            activityVisibleColumns={activityVisibleColumns}
            activityDefaultColumns={activityDefaultColumns}
            activityAvailableFormColumns={activityAvailableFormColumns}
            isSavingActivityTableLayout={isSavingActivityTableLayout}
            setIsActivityTableConfiguratorOpen={setIsActivityTableConfiguratorOpen}
            persistActivityTableLayout={persistActivityTableLayout}
            activityColumnSortFieldById={activityColumnSortFieldById}
            handleActivitySort={handleActivitySort}
            activitySortField={activitySortField}
            activitySortOrder={activitySortOrder}
            renderActivityFilterCell={renderActivityFilterCell}
            paginatedReports={paginatedReports}
            getReportStartEnd={getReportStartEnd}
            getReportDuration={getReportDuration}
            activityCostByActivityId={activityCostByActivityId}
            activityPageStartIndex={activityPageStartIndex}
            getAssigneeLabelForExport={getAssigneeLabelForExport}
            getReportFormContent={getReportFormContent}
            getReportSubmitDescription={getReportSubmitDescription}
            latestManagerCommentsByActivityId={latestManagerCommentsByActivityId}
            getActivityFormColumnValue={getActivityFormColumnValue}
            formatDateOnlyTr={formatDateOnlyTr}
            formatTimeOnlyTr={formatTimeOnlyTr}
            formatHoursTr={formatHoursTr}
            formatCurrencyTr={formatCurrencyTr}
            t={t}
            handleApproveActivity={handleApproveActivity}
            handleRejectActivity={handleRejectActivity}
            isAdmin={isAdmin}
            isManager={isManager}
            openManagerCommentModal={openManagerCommentModal}
            handleViewDetails={handleViewDetails}
            handleOpenDeleteActivityConfirm={handleOpenDeleteActivityConfirm}
            activityTabReports={activityTabReports}
            activitySummaryMode={activitySummaryMode}
            setActivitySummaryMode={setActivitySummaryMode}
            filteredActivitiesAverageDuration={filteredActivitiesAverageDuration}
            filteredActivitiesTotalDuration={filteredActivitiesTotalDuration}
            filteredActivitiesAverageCost={filteredActivitiesAverageCost}
            filteredActivitiesTotalCost={filteredActivitiesTotalCost}
            activityTotalPages={activityTotalPages}
            activityCurrentPage={activityCurrentPage}
            setActivityCurrentPage={setActivityCurrentPage}
            exportActivityTabToExcel={exportActivityTabToExcel}
            activityItemsPerPage={activityItemsPerPage}
            setActivityItemsPerPage={setActivityItemsPerPage}
            reports={effectiveActivityReports}
          />
        )}

        {mainTab === "leaves" && (
          <LeavesTab
            loading={loading}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            assigneeFilter={assigneeFilter}
            setAssigneeFilter={setAssigneeFilter}
            uniqueAssignees={uniqueAssignees}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            hasActiveFilters={hasActiveFilters}
            clearAllFilters={clearAllFilters}
            tableCardRef={tableCardRef}
            tableCardStyle={tableCardStyle}
            handleActivitySort={handleActivitySort}
            activitySortField={activitySortField}
            activitySortOrder={activitySortOrder}
            leaveRowFilters={leaveRowFilters}
            setLeaveRowFilters={setLeaveRowFilters}
            leaveSubmitDescriptionLabel={leaveSubmitDescriptionLabel}
            paginatedReports={paginatedReports}
            getReportStartEnd={getReportStartEnd}
            getReportDuration={getReportDuration}
            getReportLeaveType={getReportLeaveType}
            getReportSubmitDescription={getReportSubmitDescription}
            latestManagerCommentsByActivityId={latestManagerCommentsByActivityId}
            formatDateOnlyTr={formatDateOnlyTr}
            formatTimeOnlyTr={formatTimeOnlyTr}
            formatHoursTr={formatHoursTr}
            activityPageStartIndex={activityPageStartIndex}
            activityTabReports={activityTabReports}
            leaveSummaryMode={leaveSummaryMode}
            setLeaveSummaryMode={setLeaveSummaryMode}
            filteredLeavesAverageDuration={filteredLeavesAverageDuration}
            filteredLeavesTotalDuration={filteredLeavesTotalDuration}
            t={t}
            isAdmin={isAdmin}
            isManager={isManager}
            openManagerCommentModal={openManagerCommentModal}
            handleViewDetails={handleViewDetails}
            handleOpenDeleteActivityConfirm={handleOpenDeleteActivityConfirm}
            handleApproveActivity={handleApproveActivity}
            handleRejectActivity={handleRejectActivity}
            activityTotalPages={activityTotalPages}
            activityCurrentPage={activityCurrentPage}
            setActivityCurrentPage={setActivityCurrentPage}
            exportActivityTabToExcel={exportActivityTabToExcel}
            activityItemsPerPage={activityItemsPerPage}
            setActivityItemsPerPage={setActivityItemsPerPage}
            reports={reports}
          />
        )}

        {mainTab === "projects" && (
          <ProjectsTab
            projects={projects}
            projectSearchTerm={projectsModel.projectSearchTerm}
            setProjectSearchTerm={projectsModel.setProjectSearchTerm}
            projectWorkflowFilter={projectsModel.projectWorkflowFilter}
            setProjectWorkflowFilter={projectsModel.setProjectWorkflowFilter}
            projectArchiveFilter={projectsModel.projectArchiveFilter}
            setProjectArchiveFilter={projectsModel.setProjectArchiveFilter}
            workflowText={workflowText}
            uniqueProjectWorkflows={projectsModel.uniqueProjectWorkflows}
            projectClientFilter={projectsModel.projectClientFilter}
            setProjectClientFilter={projectsModel.setProjectClientFilter}
            uniqueClients={projectsModel.uniqueClients}
            projectManagerFilter={projectsModel.projectManagerFilter}
            setProjectManagerFilter={projectsModel.setProjectManagerFilter}
            uniqueManagers={projectsModel.uniqueManagers}
            projectStartDate={projectsModel.projectStartDate}
            setProjectStartDate={projectsModel.setProjectStartDate}
            projectEndDate={projectsModel.projectEndDate}
            setProjectEndDate={projectsModel.setProjectEndDate}
            clearProjectFilters={projectsModel.clearProjectFilters}
            filteredProjects={projectsModel.filteredProjects}
            tableCardRef={tableCardRef}
            tableCardStyle={tableCardStyle}
            loading={projectsTabLoading}
            handleProjectSort={projectsModel.handleProjectSort}
            projectSortField={projectsModel.projectSortField}
            projectSortOrder={projectsModel.projectSortOrder}
            t={t}
            projectRowFilters={projectsModel.projectRowFilters}
            setProjectRowFilters={projectsModel.setProjectRowFilters}
            canViewCosts={canViewCosts}
            clearProjectRowFilters={projectsModel.clearProjectRowFilters}
            paginatedProjects={projectsModel.paginatedProjects}
            getPlannedHours={getPlannedHours}
            projectRealizedCostByWorkflowId={projectRealizedCostByWorkflowId}
            currentAverageHourlyRate={projectsModel.currentAverageHourlyRate}
            collectionTotalsTryByWorkflowInstanceId={
              projectsModel.collectionTotalsTryByWorkflowInstanceId
            }
            getProjectFinanceSummaryForDashboard={getProjectFinanceSummaryForDashboard}
            getProjectCode={getProjectCode}
            users={users}
            formatCurrencyByCode={formatCurrencyByCode}
              getProjectCurrencyForFormData={getProjectCurrencyForFormData}
              convertTryAmountForProject={convertTryAmountForProject}
              convertTryAmountToCurrency={convertTryAmountToCurrency}
              convertAmountToTry={convertAmountToTry}
              companySettings={companySettings}
              applyCompanySettings={applyCompanySettings}
              handleViewProjectFiles={handleViewProjectFiles}
              navigate={navigate}
              handleEditProject={handleEditProject}
            projectSummaryMode={projectsModel.projectSummaryMode}
            setProjectSummaryMode={projectsModel.setProjectSummaryMode}
            filteredProjectsAverageSpentDuration={projectsModel.filteredProjectsAverageSpentDuration}
            filteredProjectsTotalSpentDuration={projectsModel.filteredProjectsTotalSpentDuration}
            filteredProjectsAveragePlannedDuration={projectsModel.filteredProjectsAveragePlannedDuration}
            filteredProjectsTotalPlannedDuration={projectsModel.filteredProjectsTotalPlannedDuration}
            formatHoursTr={formatHoursTr}
            filteredProjectsAverageRealizedCost={projectsModel.filteredProjectsAverageRealizedCost}
            filteredProjectsTotalRealizedCost={projectsModel.filteredProjectsTotalRealizedCost}
            filteredProjectsAverageInvoiceTotal={projectsModel.filteredProjectsAverageInvoiceTotal}
            filteredProjectsTotalInvoiceTotal={projectsModel.filteredProjectsTotalInvoiceTotal}
            filteredProjectsAverageCollectedAmount={projectsModel.filteredProjectsAverageCollectedAmount}
            filteredProjectsTotalCollectedAmount={projectsModel.filteredProjectsTotalCollectedAmount}
            filteredProjectsAverageRemainingCollection={projectsModel.filteredProjectsAverageRemainingCollection}
            filteredProjectsTotalRemainingCollection={projectsModel.filteredProjectsTotalRemainingCollection}
            filteredProjectsAverageNetProfit={projectsModel.filteredProjectsAverageNetProfit}
            filteredProjectsTotalNetProfit={projectsModel.filteredProjectsTotalNetProfit}
            filteredProjectsAverageEstimatedNetProfit={projectsModel.filteredProjectsAverageEstimatedNetProfit}
            filteredProjectsTotalEstimatedNetProfit={projectsModel.filteredProjectsTotalEstimatedNetProfit}
            filteredProjectsAverageRemainingEstimatedCost={projectsModel.filteredProjectsAverageRemainingEstimatedCost}
            filteredProjectsTotalRemainingEstimatedCost={projectsModel.filteredProjectsTotalRemainingEstimatedCost}
            filteredProjectsAverageEstimatedCost={projectsModel.filteredProjectsAverageEstimatedCost}
            filteredProjectsTotalEstimatedCost={projectsModel.filteredProjectsTotalEstimatedCost}
            hasActiveProjectFilters={projectsModel.hasActiveProjectFilters}
            projectTotalPages={projectsModel.projectTotalPages}
            projectCurrentPage={projectsModel.projectCurrentPage}
            setProjectCurrentPage={projectsModel.setProjectCurrentPage}
            exportProjectsToExcel={projectsModel.exportProjectsToExcel}
            projectItemsPerPage={projectsModel.projectItemsPerPage}
            setProjectItemsPerPage={projectsModel.setProjectItemsPerPage}
          />
        )}

        {mainTab === "reports" && (
          <ReportsTab
            reportMonth={reportsModel.reportMonth}
            setReportMonth={reportsModel.setReportMonth}
            reportYear={reportsModel.reportYear}
            setReportYear={reportsModel.setReportYear}
            reportView={reportsModel.reportView}
            setReportView={reportsModel.setReportView}
            reportProjectRef={reportsModel.reportProjectRef}
            reportProjectNames={reportsModel.reportProjectNames}
            reportGroupColumns={reportsModel.reportGroupColumns}
            isReportProjectOpen={reportsModel.isReportProjectOpen}
            setIsReportProjectOpen={reportsModel.setIsReportProjectOpen}
            setIsReportActivityOpen={reportsModel.setIsReportActivityOpen}
            setIsReportUserOpen={reportsModel.setIsReportUserOpen}
            reportProjectSearch={reportsModel.reportProjectSearch}
            setReportProjectSearch={reportsModel.setReportProjectSearch}
            filteredReportProjects={reportsModel.filteredReportProjects}
            toggleReportProjectName={reportsModel.toggleReportProjectName}
            setReportProjectNames={reportsModel.setReportProjectNames}
            reportActivityRef={reportsModel.reportActivityRef}
            reportActivityOptions={reportsModel.reportActivityOptions}
            fetchActivities={fetchActivities}
            isReportActivityOpen={reportsModel.isReportActivityOpen}
            reportActivitySearch={reportsModel.reportActivitySearch}
            setReportActivitySearch={reportsModel.setReportActivitySearch}
            filteredReportActivities={reportsModel.filteredReportActivities}
            reportActivityIds={reportsModel.reportActivityIds}
            toggleReportActivityId={reportsModel.toggleReportActivityId}
            setReportActivityIds={reportsModel.setReportActivityIds}
            reportUserRef={reportsModel.reportUserRef}
            reportUserOptions={reportsModel.reportUserOptions}
            fetchUsers={fetchUsers}
            isReportUserOpen={reportsModel.isReportUserOpen}
            reportUserSearch={reportsModel.reportUserSearch}
            setReportUserSearch={reportsModel.setReportUserSearch}
            filteredReportUsers={reportsModel.filteredReportUsers}
            reportUserIds={reportsModel.reportUserIds}
            toggleReportUserId={reportsModel.toggleReportUserId}
            setReportUserIds={reportsModel.setReportUserIds}
            getSelectionSummary={reportsModel.getSelectionSummary}
            reportSearch={reportsModel.reportSearch}
            setReportSearch={reportsModel.setReportSearch}
            exportReportToExcel={reportsModel.exportReportToExcel}
            t={t}
            tableCardRef={tableCardRef}
            tableCardStyle={tableCardStyle}
            visibleReportColumns={reportsModel.visibleReportColumns}
            displayMonthlyReport={reportsModel.displayMonthlyReport}
            reportLoading={reportsModel.reportLoading}
            canViewCosts={canViewCosts}
            reportMonthlyCostByUserId={reportsModel.reportMonthlyCostByUserId}
            formatCurrencyTr={formatCurrencyTr}
            reportSummaryMode={reportsModel.reportSummaryMode}
            setReportSummaryMode={reportsModel.setReportSummaryMode}
            reportSummaryDisplay={reportsModel.reportSummaryDisplay}
            reportCostDisplayValue={reportsModel.reportCostDisplayValue}
          />
        )}
      </div>

      <ProjectFilesModal
        isOpen={showProjectFilesModal}
        onClose={handleCloseProjectFilesModal}
        workflowInstanceId={selectedProjectForFiles?.project.id ?? null}
        templateWorkflowId={
          selectedProjectForFiles?.project.templateWorkflowId ?? null
        }
        projectName={selectedProjectForFiles?.projectName ?? "-"}
        projectMemberIds={selectedProjectForFiles?.project.projectMembers ?? []}
        projectManagerId={selectedProjectForFiles?.projectManagerId ?? null}
      />

      {showProjectEditModal && selectedProjectForEdit && (
        <WorkflowStartModal
          isOpen={showProjectEditModal}
          onClose={handleCloseProjectEditModal}
          projectId={selectedProjectForEdit.workflowContainerId}
          workflowId={
            selectedProjectForEdit.workflowId ||
            selectedProjectForEdit.templateWorkflowId ||
            0
          }
          workflowInstanceId={selectedProjectForEdit.id}
          isEditMode={true}
          initialData={selectedProjectForEdit.workflowContainerFormData}
          onSuccess={async () => {
            await fetchProjects();
            handleCloseProjectEditModal();
          }}
        />
      )}

      {/* Task Detail Modal */}
      <TaskDetailModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        task={selectedTask}
      />

      {/* Manager Comment Modal */}
      {managerCommentTarget && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[120] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-6 flex items-center justify-between">
              <div className="min-w-0">
                <h2 className="text-xl font-bold text-white truncate">
                  {t("dashboard:managerActivities.managerComment")}
                </h2>
                <p className="text-white/80 text-xs mt-1 truncate">
                  {(managerCommentTarget.projectName || "-") +
                    " / " +
                    (managerCommentTarget.activityTitle || "-") +
                    " / " +
                    (managerCommentTarget.assigneeName || "-")}
                </p>
              </div>
              <button
                onClick={closeManagerCommentModal}
                disabled={savingManagerComment}
                className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors disabled:opacity-60"
                aria-label="Close"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-3">
              <label className="block text-sm font-semibold text-gray-700">
                {t("dashboard:managerActivities.comment")}
              </label>
              <textarea
                value={managerCommentText}
                onChange={(e) => setManagerCommentText(e.target.value)}
                rows={5}
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                placeholder={t("dashboard:managerActivities.commentPlaceholder")}
                disabled={savingManagerComment}
              />
            </div>

            <div className="p-6 border-t border-gray-100 bg-gray-50 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={closeManagerCommentModal}
                disabled={savingManagerComment}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-semibold transition-colors disabled:opacity-60"
              >
                {t("common:cancel")}
              </button>
              <button
                type="button"
                onClick={saveManagerComment}
                disabled={savingManagerComment}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-60"
              >
                <CheckCircleIcon className="w-5 h-5" />
                {savingManagerComment ? t("common:saving") : t("common:save")}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={showDeleteActivityConfirm && !!activityReportToDelete}
        onClose={handleCancelDeleteActivity}
        onConfirm={handleConfirmDeleteActivity}
        title={t("activities:deleteActivity")}
        message={t("activities:confirmDeleteActivity")}
        confirmText={t("common:delete")}
        cancelText={t("common:cancel")}
        type="danger"
      />

      <AssignTaskModal
        isOpen={showAssignTaskModal}
        onClose={() => setShowAssignTaskModal(false)}
        onSuccess={() => {
          void refreshReports();
          void fetchProjects();
        }}
        projects={projects.map((project) => ({
          id: project.id,
          projectName: project.workflowContainerName || project.name,
          workflowName: project.templateWorkflowName || null,
          projectCode: project.projectCode || null,
          templateWorkflowId:
            project.templateWorkflowId ?? project.workflowId ?? null,
        }))}
        users={users}
        initialWorkflowInstanceId={null}
      />

      {/* Attachments Modal */}
      {selectedTaskAttachments && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[120] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-6 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-white">
                  {selectedTaskAttachments?.task.activityTitle}
                </h2>
                <p className="text-white/80 text-sm mt-1">
                  {t("dashboard:fileCount", {
                    count: selectedTaskAttachments?.attachments.length || 0,
                  })}
                </p>
              </div>
              <button
                onClick={() => setSelectedTaskAttachments(null)}
                className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {(() => {
                // Split attachments into user and template groups
                const templateAttachments: typeof selectedTaskAttachments.attachments =
                  [];
                const userAttachments: typeof selectedTaskAttachments.attachments =
                  [];

                selectedTaskAttachments?.attachments.forEach((attachment) => {
                  if (attachment.addedBy) {
                    userAttachments.push(attachment);
                  } else {
                    templateAttachments.push(attachment);
                  }
                });

                // Helper to render attachment card
                const renderAttachmentCard = (
                  attachment: any,
                  isTemplate: boolean,
                ) => {
                  let addedByName = "";
                  if (!isTemplate && attachment.addedBy) {
                    // Try to find user name
                    const userId = attachment.addedBy;
                    const task = selectedTaskAttachments.task;

                    if (task.assigneeId === userId) {
                      addedByName = task.assigneeName || "";
                    }

                    if (!addedByName && task.formSubmissions) {
                      const submission = task.formSubmissions.find(
                        (fs: any) =>
                          fs.userId === userId || fs.submittedBy === userId,
                      );
                      if (submission) addedByName = submission.userName || "";
                    }

                    if (
                      !addedByName &&
                      (task as any).allUserForms &&
                      Array.isArray((task as any).allUserForms)
                    ) {
                      const userForm = (task as any).allUserForms.find(
                        (uf: any) => uf.userId === userId,
                      );
                      if (userForm) addedByName = userForm.userName;
                    }

                    if (!addedByName) {
                      const user = users.find((u) => u.id === userId);
                      if (user) {
                        addedByName = `${user.firstName} ${user.lastName}`;
                      } else {
                        addedByName = `${t("reports:user")} ${userId}`;
                      }
                    }
                  }

                  return (
                    <button
                      key={attachment.id}
                      type="button"
                      onClick={() =>
                        void handlePreviewWorkflowAttachment(attachment)
                      }
                      disabled={previewingDriveFileId === attachment.id}
                      className={`flex flex-col p-4 rounded-lg border hover:shadow-md transition-all group relative overflow-hidden ${
                        isTemplate
                          ? "bg-blue-50 border-blue-200 hover:border-blue-300"
                          : "bg-green-50 border-green-200 hover:border-green-300"
                      }`}
                    >
                      <div className="flex items-center space-x-3 mb-3 pr-2">
                        {previewingDriveFileId === attachment.id ? (
                          <ArrowPathIcon className="w-8 h-8 flex-shrink-0 text-blue-500 animate-spin" />
                        ) : attachment.iconUrl ? (
                          <img
                            src={attachment.iconUrl}
                            alt=""
                            className="w-8 h-8 flex-shrink-0"
                          />
                        ) : (
                          <DocumentTextIcon
                            className={`w-8 h-8 flex-shrink-0 ${
                              isTemplate ? "text-blue-400" : "text-green-400"
                            }`}
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-gray-900 truncate group-hover:text-blue-700">
                            {attachment.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {attachment.mimeType
                              ?.split("/")[1]
                              ?.toUpperCase() || "FILE"}
                          </p>
                        </div>
                      </div>

                      <div className="mt-auto pt-3 border-t border-gray-200/50 flex justify-between items-center text-xs w-full">
                        <div className="text-gray-500">
                          {isTemplate ? (
                            <span className="text-blue-600 font-medium">
                              Şablon Dosyası
                            </span>
                          ) : (
                            <span className="font-medium text-green-700">
                              {addedByName}
                            </span>
                          )}
                        </div>
                        <div className="text-gray-400">
                          {new Date(attachment.addedAt).toLocaleDateString(
                            "tr-TR",
                          )}
                        </div>
                      </div>
                    </button>
                  );
                };

                return (
                  <div className="space-y-8">
                    {/* User Attachments Section */}
                    {userAttachments.length > 0 && (
                      <div>
                        <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                          <span className="bg-green-100 text-green-700 p-1.5 rounded-lg mr-2">
                            <DocumentTextIcon className="w-5 h-5" />
                          </span>
                          Kullanıcı Yüklemeleri
                          <span className="ml-2 text-sm font-normal text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                            {userAttachments.length}
                          </span>
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                          {userAttachments.map((att) =>
                            renderAttachmentCard(att, false),
                          )}
                        </div>
                      </div>
                    )}

                    {/* Template Attachments Section */}
                    {templateAttachments.length > 0 && (
                      <div>
                        <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                          <span className="bg-blue-100 text-blue-700 p-1.5 rounded-lg mr-2">
                            <DocumentTextIcon className="w-5 h-5" />
                          </span>
                          Şablon Dosyaları
                          <span className="ml-2 text-sm font-normal text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                            {templateAttachments.length}
                          </span>
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                          {templateAttachments.map((att) =>
                            renderAttachmentCard(att, true),
                          )}
                        </div>
                      </div>
                    )}

                    {userAttachments.length === 0 &&
                      templateAttachments.length === 0 && (
                        <div className="text-center py-12 text-gray-500">
                          Hiç dosya bulunamadı.
                        </div>
                      )}
                  </div>
                );
              })()}
            </div>

            {/* Modal Footer */}
            <div className="border-t p-4 bg-gray-50">
              <button
                onClick={() => setSelectedTaskAttachments(null)}
                className="w-full px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium rounded-lg transition-colors"
              >
                {t("common:close")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
